// D&D 5e SRD Character Data
// Contains races, classes, backgrounds with their features and proficiencies

export const SRD_RACES = {
  'Dragonborn': {
    abilityScoreIncrease: { str: 2, cha: 1 },
    size: 'Medium',
    speed: 30,
    features: ['Draconic Ancestry', 'Breath Weapon', 'Damage Resistance'],
    languages: ['Common', 'Draconic'],
    // [P2.11 fix Apr 2026]: Each ancestry now exposes a per-element
    // `features` array so the wizard render path
    // (CharacterCreationWizard.js:1691 — subraces[X].features.map) shows
    // the player which element they resist + breath. damageType +
    // breathWeapon retained as machine-readable seeds for downstream
    // helpers (getBreathWeaponDamageType / getBreathWeaponShape in
    // contentResolver.js).
    subraces: {
      'Black': { damageType: 'Acid', breathWeapon: '5 by 30 ft. line (Dex. save)', features: ['Draconic Resistance (Acid)', 'Draconic Breath Weapon (Acid Line)'] },
      'Blue': { damageType: 'Lightning', breathWeapon: '5 by 30 ft. line (Dex. save)', features: ['Draconic Resistance (Lightning)', 'Draconic Breath Weapon (Lightning Line)'] },
      'Brass': { damageType: 'Fire', breathWeapon: '5 by 30 ft. line (Dex. save)', features: ['Draconic Resistance (Fire)', 'Draconic Breath Weapon (Fire Line)'] },
      'Bronze': { damageType: 'Lightning', breathWeapon: '5 by 30 ft. line (Dex. save)', features: ['Draconic Resistance (Lightning)', 'Draconic Breath Weapon (Lightning Line)'] },
      'Copper': { damageType: 'Acid', breathWeapon: '5 by 30 ft. line (Dex. save)', features: ['Draconic Resistance (Acid)', 'Draconic Breath Weapon (Acid Line)'] },
      'Gold': { damageType: 'Fire', breathWeapon: '15 ft. cone (Dex. save)', features: ['Draconic Resistance (Fire)', 'Draconic Breath Weapon (Fire Cone)'] },
      'Green': { damageType: 'Poison', breathWeapon: '15 ft. cone (Con. save)', features: ['Draconic Resistance (Poison)', 'Draconic Breath Weapon (Poison Cone)'] },
      'Red': { damageType: 'Fire', breathWeapon: '15 ft. cone (Dex. save)', features: ['Draconic Resistance (Fire)', 'Draconic Breath Weapon (Fire Cone)'] },
      'Silver': { damageType: 'Cold', breathWeapon: '15 ft. cone (Con. save)', features: ['Draconic Resistance (Cold)', 'Draconic Breath Weapon (Cold Cone)'] },
      'White': { damageType: 'Cold', breathWeapon: '15 ft. cone (Con. save)', features: ['Draconic Resistance (Cold)', 'Draconic Breath Weapon (Cold Cone)'] }
    }
  },
  'Dwarf': {
    abilityScoreIncrease: { con: 2 },
    size: 'Medium',
    speed: 25,
    darkvision: 60, // [FIX APR 2026 — AUDIT H-8]: Data-driven darkvision for custom race support
    features: ['Darkvision', 'Dwarven Resilience', 'Dwarven Combat Training', 'Tool Proficiency', 'Stonecunning'],
    proficiencies: {
      weapons: ['Battleaxe', 'Handaxe', 'Light hammer', 'Warhammer'],
      tools: ['Smith\'s tools OR Brewer\'s supplies OR Mason\'s tools']
    },
    languages: ['Common', 'Dwarvish'],
    savingThrows: { poison: 'advantage' },
    subraces: {
      'Hill Dwarf': {
        abilityScoreIncrease: { wis: 1 },
        features: ['Dwarven Toughness'],
        hitPointBonus: 1
      },
      'Mountain Dwarf': {
        abilityScoreIncrease: { str: 2 },
        proficiencies: { armor: ['Light armor', 'Medium armor'] }
      }
    }
  },
  'Elf': {
    abilityScoreIncrease: { dex: 2 },
    size: 'Medium',
    speed: 30,
    darkvision: 60, // [FIX APR 2026 — AUDIT H-8]: Data-driven darkvision for custom race support
    features: ['Darkvision', 'Keen Senses', 'Fey Ancestry', 'Trance'],
    proficiencies: { skills: ['Perception'] },
    languages: ['Common', 'Elvish'],
    savingThrows: { charm: 'advantage', sleep: 'immunity' },
    subraces: {
      'High Elf': {
        abilityScoreIncrease: { int: 1 },
        proficiencies: { weapons: ['Longsword', 'Shortsword', 'Shortbow', 'Longbow'] },
        features: ['Cantrip'],
        languages: ['One extra language']
      },
      'Wood Elf': {
        abilityScoreIncrease: { wis: 1 },
        speed: 35,
        proficiencies: { weapons: ['Longsword', 'Shortsword', 'Shortbow', 'Longbow'] },
        features: ['Mask of the Wild']
      },
      'Dark Elf (Drow)': {
        abilityScoreIncrease: { cha: 1 },
        darkvision: 120,
        features: ['Superior Darkvision', 'Sunlight Sensitivity', 'Drow Magic'],
        proficiencies: { weapons: ['Rapier', 'Shortsword', 'Hand crossbow'] }
      }
    }
  },
  'Gnome': {
    abilityScoreIncrease: { int: 2 },
    size: 'Small',
    speed: 25,
    darkvision: 60, // [FIX APR 2026 — AUDIT H-8]: Data-driven darkvision for custom race support
    features: ['Darkvision', 'Gnome Cunning'],
    languages: ['Common', 'Gnomish'],
    savingThrows: { 'int_magic': 'advantage', 'wis_magic': 'advantage', 'cha_magic': 'advantage' },
    subraces: {
      'Forest Gnome': {
        abilityScoreIncrease: { dex: 1 },
        features: ['Natural Illusionist', 'Speak with Small Beasts']
      },
      'Rock Gnome': {
        abilityScoreIncrease: { con: 1 },
        features: ['Artificer\'s Lore', 'Tinker']
      }
    }
  },
  'Half-Elf': {
    abilityScoreIncrease: { cha: 2, choice: [1, 1] }, // +1 to two different scores of choice
    size: 'Medium',
    speed: 30,
    darkvision: 60, // [FIX APR 2026 — AUDIT H-8]: Data-driven darkvision for custom race support
    features: ['Darkvision', 'Fey Ancestry', 'Skill Versatility'],
    proficiencies: { skills: ['Choose any two'] },
    languages: ['Common', 'Elvish', 'One extra language']
  },
  'Half-Orc': {
    abilityScoreIncrease: { str: 2, con: 1 },
    size: 'Medium',
    speed: 30,
    darkvision: 60, // [FIX APR 2026 — AUDIT H-8]: Data-driven darkvision for custom race support
    features: ['Darkvision', 'Menacing', 'Relentless Endurance', 'Savage Attacks'],
    proficiencies: { skills: ['Intimidation'] },
    languages: ['Common', 'Orc']
  },
  'Halfling': {
    abilityScoreIncrease: { dex: 2 },
    size: 'Small',
    speed: 25,
    features: ['Lucky', 'Brave', 'Halfling Nimbleness'],
    languages: ['Common', 'Halfling'],
    savingThrows: { frightened: 'advantage' },
    subraces: {
      'Lightfoot': {
        abilityScoreIncrease: { cha: 1 },
        features: ['Naturally Stealthy']
      },
      'Stout': {
        abilityScoreIncrease: { con: 1 },
        features: ['Stout Resilience'],
        savingThrows: { poison: 'advantage' }
      }
    }
  },
  'Human': {
    // [FIX 2025-10-21]: Fixed Human race implementation per D&D 5e SRD rules
    // Standard human gets +1 to all abilities OR Variant gets +1 to two chosen, feat, and skill
    abilityScoreIncrease: { str: 1, dex: 1, con: 1, int: 1, wis: 1, cha: 1 },
    size: 'Medium',
    speed: 30,
    languages: ['Common'],
    features: [],
    // Humans use a subrace system to differentiate Standard from Variant
    subraces: {
      'Standard': {
        // Standard human already gets +1 to all from base race
        features: ['Ability Score Increase (+1 to all abilities)'],
        languages: ['One extra language of your choice']
      },
      'Variant': {
        // Variant human overrides the +1 to all, gets +1 to two chosen abilities
        variantBonuses: true, // [FIX APR 2026 — F-5]: Property-based detection for custom races with similar mechanics
        abilityScoreIncrease: { override: true }, // Special flag to override parent bonuses
        features: ['Feat', 'Skill Proficiency', 'Ability Score Increase (+1 to two different scores of your choice)'],
        customChoices: {
          abilityScores: { count: 2, amount: 1, different: true },
          feat: { count: 1, atLevel: 1 },
          skill: { count: 1, any: true }
        },
        languages: ['One extra language of your choice']
      }
    }
  },
  'Tiefling': {
    abilityScoreIncrease: { int: 1, cha: 2 },
    size: 'Medium',
    speed: 30,
    darkvision: 60, // [FIX APR 2026 — AUDIT H-8]: Data-driven darkvision for custom race support
    features: ['Darkvision', 'Hellish Resistance', 'Infernal Legacy'],
    damageResistance: ['Fire'],
    languages: ['Common', 'Infernal']
  }
};

export const SRD_CLASSES = {
  'Barbarian': {
    hitDice: '1d12',
    subclassLevel: 3,
    startingGold: '2d4',
    unarmoredDefense: { abilities: ['dexterity', 'constitution'], noShield: false },
    // [PHASE B / #062 — 2026-05-07]: rageDamageProgression removed.
    //   #042 moved the source of truth to
    //   `SRD_CLASS_FEATURES.Barbarian.Rage.rageDamageByLevel` and the
    //   effectRegistry resolver consumes that. This field was dead data.
    rageResistances: ['bludgeoning', 'piercing', 'slashing'],
    // [PHASE B / #022 — 2026-05-06]: Combat-toggle ownership data block.
    //   Replaces hardcoded `c.className === 'Barbarian'` dispatch in
    //   src/data/combatToggles.js. Reckless Attack is PHB L2+.
    combatToggles: [
      { flag: 'recklessAttackActive', minLevel: 2 },
    ],
    // [PHASE B / #044 — MAY 2026]: Primal Champion (Barbarian L20 — PHB p.49).
    //   STR +4 (cap 24) and CON +4 (cap 24). Pre-fix, App.jsx hardcoded
    //   the bump inside handleLevelUpComplete as a `className === 'Barbarian'
    //   && level === 20` branch. The data block + closed-enum action
    //   `autoStatBoost` lets future classes (homebrew or 2024 PHB) add their
    //   own auto-bumps via data alone. Helper:
    //     src/utils/autoStatBoost.js::applyLevelProgressionAutoStatBoosts
    levelProgression: [
      { atLevel: 20, action: 'autoStatBoost', stat: 'strength', amount: 4, cap: 24 },
      { atLevel: 20, action: 'autoStatBoost', stat: 'constitution', amount: 4, cap: 24 },
    ],
    primaryAbility: 'Strength',
    savingThrows: ['str', 'con'],
    proficiencies: {
      armor: ['Light armor', 'Medium armor', 'Shields'],
      weapons: ['Simple weapons', 'Martial weapons'],
      tools: [],
      skills: {
        number: 2,
        options: ['Animal Handling', 'Athletics', 'Intimidation', 'Nature', 'Perception', 'Survival']
      }
    },
    startingEquipment: [
      { choice: ['Greataxe', 'Any martial melee weapon'] },
      { choice: ['Two handaxes', 'Any simple weapon'] },
      'Explorer\'s pack',
      '4 javelins'
    ],
    features: {
      1: ['Rage', 'Unarmored Defense'],
      2: ['Reckless Attack', 'Danger Sense'],
      3: ['Primal Path'],
      4: ['Ability Score Improvement'],
      5: ['Extra Attack', 'Fast Movement'],
      6: ['Path feature'],
      7: ['Feral Instinct'],
      8: ['Ability Score Improvement'],
      9: ['Brutal Critical (1 die)'],
      10: ['Path feature'],
      11: ['Relentless Rage'],
      12: ['Ability Score Improvement'],
      13: ['Brutal Critical (2 dice)'],
      14: ['Path feature'],
      15: ['Persistent Rage'],
      16: ['Ability Score Improvement'],
      17: ['Brutal Critical (3 dice)'],
      18: ['Indomitable Might'],
      19: ['Ability Score Improvement'],
      20: ['Primal Champion']
    },
    // [PURPOSE]: D&D 5e SRD Barbarian Primal Paths
    // [WHY]: Barbarians choose their path at level 3
    subclasses: {
      'Path of the Berserker': {
        description: 'Violence is your path to greatness. You channel uncontrollable fury into battle rage, becoming an unstoppable force.',
        features: {
          3: ['Frenzy'],
          6: ['Mindless Rage'],
          10: ['Intimidating Presence'],
          14: ['Retaliation']
        }
      },
      'Path of the Totem Warrior': {
        description: 'Accept a spirit animal as your guide and protector. You gain the power of totem animals while raging.',
        totemChoices: { 3: 1, 6: 1, 14: 1 }, // Choose spirit animal at these levels
        totemOptions: ['Bear', 'Eagle', 'Wolf'], // Available totems
        features: {
          3: ['Spirit Seeker', 'Totem Spirit'],
          6: ['Aspect of the Beast'],
          10: ['Spirit Walker'],
          14: ['Totemic Attunement']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Path of the Beast': {
        description: 'Walk the path of a feral warrior, channeling the beast within. You manifest natural weapons and primal adaptations.',
        features: {
          3: ['Form of the Beast'],
          6: ['Bestial Soul'],
          10: ['Infectious Fury'],
          14: ['Call the Hunt']
        }
      },
      'Path of Wild Magic': {
        description: 'The wild influence of magic has transformed you, suffusing you with arcane power that churns within you.',
        features: {
          3: ['Magic Awareness', 'Wild Surge'],
          6: ['Bolstering Magic'],
          10: ['Unstable Backlash'],
          14: ['Controlled Surge']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Path of the Ancestral Guardian': {
        description: 'Some barbarians hail from cultures that revere their ancestors. These tribes teach that warriors of the past linger in the world as spirits.',
        features: {
          3: ['Ancestral Protectors'],
          6: ['Spirit Shield'],
          10: ['Consult the Spirits'],
          14: ['Vengeful Ancestors']
        }
      },
      'Path of the Storm Herald': {
        description: 'Filled with a rage that channels primal magic, storm heralds create powerful magical effects emanating from you.',
        features: {
          3: ['Storm Aura'],
          6: ['Storm Soul'],
          10: ['Shielding Storm'],
          14: ['Raging Storm']
        }
      },
      'Path of the Zealot': {
        description: 'Fueled by a religious fury, zealots are warriors who channel their rage into powerful displays of divine power.',
        features: {
          3: ['Divine Fury', 'Warrior of the Gods'],
          6: ['Fanatical Focus'],
          10: ['Zealous Presence'],
          14: ['Rage Beyond Death']
        }
      }
    }
  },
  'Bard': {
    hitDice: '1d8',
    subclassLevel: 3,
    startingGold: '5d4',
    primaryAbility: 'Charisma',
    savingThrows: ['dex', 'cha'],
    // [#97 2026-05-19]: Help-text moved off CCW string-equality gates onto class data.
    //   Bard subclass is at L3 not L1, so no subclassChoiceTitle/Help here.
    spellsSourceHelp: ' You learn magical secrets through music and performance.',
    spellSuggestionTip: ' Utility spells like Healing Word and Dissonant Whispers are excellent picks.',
    // [PHASE B / #036 — 2026-05-06]: PartyPanel auras data block.
    //   See Paladin entry for schema notes.
    auras: [
      {
        name: 'Countercharm',
        minLevel: 6,
        radius: 30,
        descriptionTemplate: 'Action: until end of next turn, you and creatures within ${radius} ft have advantage on saves vs frightened/charmed. (PHB p.54)',
        color: 'bg-purple-900/30 border-purple-700/40 text-purple-200',
      },
    ],
    // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch
    //   data block. Pre-#037 these were hardcoded `className === 'Bard'
    //   && classLevel === N` branches inside three wizard helpers.
    //   Closed `action` enum maps to wizard renderers.
    levelProgression: [
      { atLevel: 3,  action: 'expertisePicks', count: 2 },
      { atLevel: 10, action: 'expertisePicks', count: 2 },
      { atLevel: 10, action: 'magicalSecrets', count: 2, dialogTitle: 'Magical Secrets', maxSpellLevel: 5 },
      { atLevel: 14, action: 'magicalSecrets', count: 2, dialogTitle: 'Magical Secrets', maxSpellLevel: 7 },
      { atLevel: 18, action: 'magicalSecrets', count: 2, dialogTitle: 'Magical Secrets', maxSpellLevel: 9 },
    ],
    proficiencies: {
      armor: ['Light armor'],
      weapons: ['Simple weapons', 'Hand crossbows', 'Longswords', 'Rapiers', 'Shortswords'],
      tools: ['Three musical instruments of your choice'],
      skills: {
        number: 3,
        options: 'Any'
      }
    },
    spellcasting: {
      ability: 'cha',
      casterType: 'full',
      cantrips: { 1: 2, 4: 3, 10: 4 },
      spellsKnown: { 1: 4, 2: 5, 3: 6, 4: 7, 5: 8, 6: 9, 7: 10, 8: 11, 9: 12, 10: 14, 11: 15, 13: 16, 14: 18, 15: 19, 17: 20, 18: 22 }
    },
    startingEquipment: [
      { choice: ['Rapier', 'Longsword', 'Any simple weapon'] },
      { choice: ['Diplomat\'s pack', 'Entertainer\'s pack'] },
      { choice: ['Lute', 'Any other musical instrument'] },
      'Leather armor',
      'Dagger'
    ],
    features: {
      1: ['Bardic Inspiration (d6)', 'Spellcasting'],
      2: ['Jack of All Trades', 'Song of Rest (d6)'],
      3: ['Bard College', 'Expertise'],
      4: ['Ability Score Improvement'],
      5: ['Bardic Inspiration (d8)', 'Font of Inspiration'],
      6: ['Countercharm', 'College feature'],
      7: [],
      8: ['Ability Score Improvement'],
      9: ['Song of Rest (d8)'],
      10: ['Bardic Inspiration (d10)', 'Expertise', 'Magical Secrets'],
      11: [],
      12: ['Ability Score Improvement'],
      13: ['Song of Rest (d10)'],
      14: ['Magical Secrets', 'College feature'],
      15: ['Bardic Inspiration (d12)'],
      16: ['Ability Score Improvement'],
      17: ['Song of Rest (d12)'],
      18: ['Magical Secrets'],
      19: ['Ability Score Improvement'],
      20: ['Superior Inspiration']
    },
    // [PURPOSE]: D&D 5e SRD Bard Colleges
    // [WHY]: Bards choose their college at level 3
    subclasses: {
      'College of Lore': {
        description: 'Masters of lore and knowledge. You collect stories, songs, and secrets from every source you can find.',
        features: {
          3: ['Bonus Proficiencies', 'Cutting Words'],
          6: ['Additional Magical Secrets'],
          14: ['Peerless Skill']
        }
      },
      'College of Valor': {
        description: 'Inspiring warriors who learn to inspire others in battle. You blend magic and martial prowess.',
        features: {
          3: ['Bonus Proficiencies', 'Combat Inspiration'],
          6: ['Extra Attack'],
          14: ['Battle Magic']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'College of Creation': {
        description: 'Believe in the power of creation and use performance to invoke the magic of creation. You can bring objects and creatures into being.',
        features: {
          3: ['Mote of Potential', 'Performance of Creation'],
          6: ['Animating Performance'],
          14: ['Creative Crescendo']
        }
      },
      'College of Eloquence': {
        description: 'Master the art of oratory. Your carefully chosen words can inspire allies and undermine enemies with equal grace.',
        features: {
          3: ['Silver Tongue', 'Unsettling Words'],
          6: ['Unfailing Inspiration'],
          14: ['Universal Speech']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'College of Glamour': {
        description: 'Tap into the beguiling magic of the Feywild. Your performances captivate and mystify, weaving fey magic into your words and music.',
        features: {
          3: ['Mantle of Inspiration', 'Enthralling Performance'],
          6: ['Mantle of Majesty'],
          14: ['Unbreakable Majesty']
        }
      },
      'College of Swords': {
        description: 'Entertain through daring feats of weapon prowess. Blades swirl around you, and you dazzle your foes with your weapon and wit.',
        features: {
          3: ['Bonus Proficiencies', 'Fighting Style', 'Blade Flourish'],
          6: ['Extra Attack'],
          14: ['Master\'s Flourish']
        }
      },
      'College of Whispers': {
        description: 'Use knowledge of people to manipulate and terrify. You learn to infuse your words with frightening magic.',
        features: {
          3: ['Psychic Blades', 'Words of Terror'],
          6: ['Mantle of Whispers'],
          14: ['Shadow Lore']
        }
      }
    }
  },
  'Cleric': {
    hitDice: '1d8',
    subclassLevel: 1,
    startingGold: '5d4',
    primaryAbility: 'Wisdom',
    savingThrows: ['wis', 'cha'],
    // [#97 2026-05-19]: Help-text moved off CCW string-equality gates onto class data.
    //   Now any CW custom class can author its own L1-subclass-picker copy.
    subclassChoiceTitle: 'Divine Domain',
    subclassChoiceHelp: 'Your divine domain shapes how you serve your deity and grants domain spells plus a level-1 feature.',
    proficiencies: {
      armor: ['Light armor', 'Medium armor', 'Shields'],
      weapons: ['Simple weapons'],
      tools: [],
      skills: {
        number: 2,
        options: ['History', 'Insight', 'Medicine', 'Persuasion', 'Religion']
      }
    },
    spellcasting: {
      ability: 'wis',
      casterType: 'full',
      cantrips: { 1: 3, 4: 4, 10: 5 },
      preparedSpells: 'Wisdom modifier + cleric level'
    },
    startingEquipment: [
      { choice: ['Mace', 'Warhammer (if proficient)'] },
      { choice: ['Scale mail', 'Leather armor', 'Chain mail (if proficient)'] },
      { choice: ['Light crossbow and 20 bolts', 'Any simple weapon'] },
      { choice: ['Priest\'s pack', 'Explorer\'s pack'] },
      'Shield',
      'Holy symbol'
    ],
    features: {
      1: ['Spellcasting', 'Divine Domain'],
      2: ['Channel Divinity (1/rest)', 'Domain feature'],
      3: [],
      4: ['Ability Score Improvement'],
      5: ['Destroy Undead (CR 1/2)'],
      6: ['Channel Divinity (2/rest)', 'Domain feature'],
      7: [],
      8: ['Ability Score Improvement', 'Destroy Undead (CR 1)', 'Domain feature'],
      9: [],
      10: ['Divine Intervention'],
      11: ['Destroy Undead (CR 2)'],
      12: ['Ability Score Improvement'],
      13: [],
      14: ['Destroy Undead (CR 3)'],
      15: [],
      16: ['Ability Score Improvement'],
      17: ['Destroy Undead (CR 4)', 'Domain feature'],
      18: ['Channel Divinity (3/rest)'],
      19: ['Ability Score Improvement'],
      20: ['Divine Intervention improvement']
    },
    // [PURPOSE]: D&D 5e SRD Cleric Divine Domains
    // [WHY]: Clerics choose their domain at level 1
    subclasses: {
      'Life Domain': {
        description: 'Promote vitality and health. Your healing spells are more effective and you can protect those who would fall.',
        domainSpells: { 1: ['Bless', 'Cure Wounds'], 3: ['Lesser Restoration', 'Spiritual Weapon'], 5: ['Beacon of Hope', 'Revivify'], 7: ['Death Ward', 'Guardian of Faith'], 9: ['Mass Cure Wounds', 'Raise Dead'] },
        features: {
          1: ['Bonus Proficiency', 'Disciple of Life'],
          2: ['Channel Divinity: Preserve Life'],
          6: ['Blessed Healer'],
          8: ['Divine Strike'],
          17: ['Supreme Healing']
        }
      },
      'Knowledge Domain': {
        description: 'Value learning and understanding. You master skills and can read thoughts and memories.',
        domainSpells: { 1: ['Command', 'Identify'], 3: ['Augury', 'Suggestion'], 5: ['Nondetection', 'Speak with Dead'], 7: ['Arcane Eye', 'Confusion'], 9: ['Legend Lore', 'Scrying'] },
        features: {
          1: ['Blessings of Knowledge'],
          2: ['Channel Divinity: Knowledge of the Ages'],
          6: ['Channel Divinity: Read Thoughts'],
          8: ['Potent Spellcasting'],
          17: ['Visions of the Past']
        }
      },
      'Light Domain': {
        description: 'Oppose darkness and evil. Your magic blazes with radiance that reveals and burns.',
        domainSpells: { 1: ['Burning Hands', 'Faerie Fire'], 3: ['Flaming Sphere', 'Scorching Ray'], 5: ['Daylight', 'Fireball'], 7: ['Guardian of Faith', 'Wall of Fire'], 9: ['Flame Strike', 'Scrying'] },
        features: {
          1: ['Bonus Cantrip', 'Warding Flare'],
          2: ['Channel Divinity: Radiance of the Dawn'],
          6: ['Improved Flare'],
          8: ['Potent Spellcasting'],
          17: ['Corona of Light']
        }
      },
      'Nature Domain': {
        description: 'Embody the power of the natural world. You command plants and beasts, and can channel elemental forces.',
        domainSpells: { 1: ['Animal Friendship', 'Speak with Animals'], 3: ['Barkskin', 'Spike Growth'], 5: ['Plant Growth', 'Wind Wall'], 7: ['Dominate Beast', 'Grasping Vine'], 9: ['Insect Plague', 'Tree Stride'] },
        features: {
          1: ['Acolyte of Nature', 'Bonus Proficiency'],
          2: ['Channel Divinity: Charm Animals and Plants'],
          6: ['Dampen Elements'],
          8: ['Divine Strike'],
          17: ['Master of Nature']
        }
      },
      'Tempest Domain': {
        description: 'Wield the fury of the storm. Your magic commands thunder and lightning with devastating effect.',
        domainSpells: { 1: ['Fog Cloud', 'Thunderwave'], 3: ['Gust of Wind', 'Shatter'], 5: ['Call Lightning', 'Sleet Storm'], 7: ['Control Water', 'Ice Storm'], 9: ['Destructive Wave', 'Insect Plague'] },
        features: {
          1: ['Bonus Proficiencies', 'Wrath of the Storm'],
          2: ['Channel Divinity: Destructive Wrath'],
          6: ['Thunderbolt Strike'],
          8: ['Divine Strike'],
          17: ['Stormborn']
        }
      },
      'Trickery Domain': {
        description: 'Master of illusion and deception. You use trickery and stealth to achieve your goals.',
        domainSpells: { 1: ['Charm Person', 'Disguise Self'], 3: ['Mirror Image', 'Pass without Trace'], 5: ['Blink', 'Dispel Magic'], 7: ['Dimension Door', 'Polymorph'], 9: ['Dominate Person', 'Modify Memory'] },
        features: {
          1: ['Blessing of the Trickster'],
          2: ['Channel Divinity: Invoke Duplicity'],
          6: ['Channel Divinity: Cloak of Shadows'],
          8: ['Divine Strike'],
          17: ['Improved Duplicity']
        }
      },
      'War Domain': {
        description: 'Champion of battle. You inspire warriors and crush the forces of your enemies.',
        domainSpells: { 1: ['Divine Favor', 'Shield of Faith'], 3: ['Magic Weapon', 'Spiritual Weapon'], 5: ['Crusader\'s Mantle', 'Spirit Guardians'], 7: ['Freedom of Movement', 'Stoneskin'], 9: ['Flame Strike', 'Hold Monster'] },
        features: {
          1: ['Bonus Proficiencies', 'War Priest'],
          2: ['Channel Divinity: Guided Strike'],
          6: ['Channel Divinity: War God\'s Blessing'],
          8: ['Divine Strike'],
          17: ['Avatar of Battle']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Peace Domain': {
        description: 'Embody the tenets of peace and strive to end conflict. You protect and support your allies through harmonious magic.',
        domainSpells: { 1: ['Heroism', 'Sanctuary'], 3: ['Aid', 'Warding Bond'], 5: ['Beacon of Hope', 'Sending'], 7: ['Aura of Purity', 'Otiluke\'s Resilient Sphere'], 9: ['Greater Restoration', 'Rary\'s Telepathic Bond'] },
        features: {
          1: ['Implement of Peace', 'Emboldening Bond'],
          2: ['Channel Divinity: Balm of Peace'],
          6: ['Protective Bond'],
          8: ['Potent Spellcasting'],
          17: ['Expansive Bond']
        }
      },
      'Twilight Domain': {
        description: 'Find beauty and comfort in darkness. You bring twilight to your enemies and respite to those who rest beneath your vigil.',
        domainSpells: { 1: ['Faerie Fire', 'Sleep'], 3: ['Moonbeam', 'See Invisibility'], 5: ['Aura of Vitality', 'Leomund\'s Tiny Hut'], 7: ['Aura of Life', 'Greater Invisibility'], 9: ['Circle of Power', 'Mislead'] },
        features: {
          1: ['Bonus Proficiencies', 'Eyes of Night', 'Vigilant Blessing'],
          2: ['Channel Divinity: Twilight Sanctuary'],
          6: ['Steps of Night'],
          8: ['Divine Strike'],
          17: ['Twilight Shroud']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Forge Domain': {
        description: 'Serve deities of the forge and creation. You can bless weapons and armor, transforming mundane items into magical ones.',
        domainSpells: { 1: ['Identify', 'Searing Smite'], 3: ['Heat Metal', 'Magic Weapon'], 5: ['Elemental Weapon', 'Protection from Energy'], 7: ['Fabricate', 'Wall of Fire'], 9: ['Animate Objects', 'Creation'] },
        features: {
          1: ['Bonus Proficiencies', 'Blessing of the Forge'],
          2: ['Channel Divinity: Artisan\'s Blessing'],
          6: ['Soul of the Forge'],
          8: ['Divine Strike'],
          17: ['Saint of Forge and Fire']
        }
      },
      'Grave Domain': {
        description: 'Walk the line between life and death. You keep the balance, denying undeath and preserving the sanctity of death.',
        domainSpells: { 1: ['Bane', 'False Life'], 3: ['Gentle Repose', 'Ray of Enfeeblement'], 5: ['Revivify', 'Vampiric Touch'], 7: ['Blight', 'Death Ward'], 9: ['Antilife Shell', 'Raise Dead'] },
        features: {
          1: ['Circle of Mortality', 'Eyes of the Grave'],
          2: ['Channel Divinity: Path to the Grave'],
          6: ['Sentinel at Death\'s Door'],
          8: ['Potent Spellcasting'],
          17: ['Keeper of Souls']
        }
      }
    }
  },
  'Druid': {
    hitDice: '1d8',
    subclassLevel: 2,
    startingGold: '2d4',
    primaryAbility: 'Wisdom',
    savingThrows: ['int', 'wis'],
    proficiencies: {
      armor: ['Light armor (non-metal)', 'Medium armor (non-metal)', 'Shields (non-metal)'],
      weapons: ['Clubs', 'Daggers', 'Darts', 'Javelins', 'Maces', 'Quarterstaffs', 'Scimitars', 'Sickles', 'Slings', 'Spears'],
      tools: ['Herbalism kit'],
      skills: {
        number: 2,
        options: ['Arcana', 'Animal Handling', 'Insight', 'Medicine', 'Nature', 'Perception', 'Religion', 'Survival']
      }
    },
    spellcasting: {
      ability: 'wis',
      casterType: 'full',
      cantrips: { 1: 2, 4: 3, 10: 4 },
      preparedSpells: 'Wisdom modifier + druid level'
    },
    startingEquipment: [
      { choice: ['Wooden shield', 'Any simple weapon'] },
      { choice: ['Scimitar', 'Any simple melee weapon'] },
      'Leather armor',
      'Explorer\'s pack',
      'Druidic focus'
    ],
    features: {
      1: ['Druidic', 'Spellcasting'],
      2: ['Wild Shape', 'Druid Circle'],
      3: [],
      4: ['Wild Shape improvement', 'Ability Score Improvement'],
      5: [],
      6: ['Circle feature'],
      7: [],
      8: ['Wild Shape improvement', 'Ability Score Improvement'],
      9: [],
      10: ['Circle feature'],
      11: [],
      12: ['Ability Score Improvement'],
      13: [],
      14: ['Circle feature'],
      15: [],
      16: ['Ability Score Improvement'],
      17: [],
      18: ['Timeless Body', 'Beast Spells'],
      19: ['Ability Score Improvement'],
      20: ['Archdruid']
    },
    // [PURPOSE]: D&D 5e SRD Druid Circles
    // [WHY]: Druids choose their circle at level 2
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'Circle of the Land': {
        description: 'Mystics whose magic is influenced by the land where they were initiated. You draw power from the terrain itself and can recover spell slots faster.',
        landChoices: { 2: 1 }, // Choose land type at level 2
        landOptions: ['Arctic', 'Coast', 'Desert', 'Forest', 'Grassland', 'Mountain', 'Swamp', 'Underdark'],
        circleSpells: {
          'Arctic': { 3: ['Hold Person', 'Spike Growth'], 5: ['Sleet Storm', 'Slow'], 7: ['Freedom of Movement', 'Ice Storm'], 9: ['Commune with Nature', 'Cone of Cold'] },
          'Coast': { 3: ['Mirror Image', 'Misty Step'], 5: ['Water Breathing', 'Water Walk'], 7: ['Control Water', 'Freedom of Movement'], 9: ['Conjure Elemental', 'Scrying'] },
          'Desert': { 3: ['Blur', 'Silence'], 5: ['Create Food and Water', 'Protection from Energy'], 7: ['Blight', 'Hallucinatory Terrain'], 9: ['Insect Plague', 'Wall of Stone'] },
          'Forest': { 3: ['Barkskin', 'Spider Climb'], 5: ['Call Lightning', 'Plant Growth'], 7: ['Divination', 'Freedom of Movement'], 9: ['Commune with Nature', 'Tree Stride'] },
          'Grassland': { 3: ['Invisibility', 'Pass without Trace'], 5: ['Daylight', 'Haste'], 7: ['Divination', 'Freedom of Movement'], 9: ['Dream', 'Insect Plague'] },
          'Mountain': { 3: ['Spider Climb', 'Spike Growth'], 5: ['Lightning Bolt', 'Meld into Stone'], 7: ['Stone Shape', 'Stoneskin'], 9: ['Passwall', 'Wall of Stone'] },
          'Swamp': { 3: ['Darkness', 'Melf\'s Acid Arrow'], 5: ['Water Walk', 'Stinking Cloud'], 7: ['Freedom of Movement', 'Locate Creature'], 9: ['Insect Plague', 'Scrying'] },
          'Underdark': { 3: ['Spider Climb', 'Web'], 5: ['Gaseous Form', 'Stinking Cloud'], 7: ['Greater Invisibility', 'Stone Shape'], 9: ['Cloudkill', 'Insect Plague'] }
        },
        features: {
          2: ['Bonus Cantrip', 'Natural Recovery'],
          6: ['Land\'s Stride'],
          10: ['Nature\'s Ward'],
          14: ['Nature\'s Sanctuary']
        }
      },
      'Circle of the Moon': {
        description: 'Fierce guardians of the wilderness who excel at transforming into powerful beasts. Your Wild Shape is more versatile and you can use it in combat.',
        features: {
          2: ['Combat Wild Shape', 'Circle Forms'],
          6: ['Primal Strike'],
          10: ['Elemental Wild Shape'],
          14: ['Thousand Forms']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Circle of Stars': {
        description: 'Gain the ability to read the stars and draw on their power. You can take on luminous forms that enhance your magic.',
        features: {
          2: ['Star Map', 'Starry Form'],
          6: ['Cosmic Omen'],
          10: ['Twinkling Constellations'],
          14: ['Full of Stars']
        }
      },
      'Circle of Wildfire': {
        description: 'Understand that destruction is sometimes the precursor to creation. You summon a primal spirit that helps you spread flames.',
        features: {
          2: ['Circle Spells', 'Summon Wildfire Spirit'],
          6: ['Enhanced Bond'],
          10: ['Cauterizing Flames'],
          14: ['Blazing Revival']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Circle of Dreams': {
        description: 'Druids who are members of the Circle of Dreams hail from regions that have strong ties to the Feywild. You mend wounds and bring joy.',
        features: {
          2: ['Balm of the Summer Court'],
          6: ['Hearth of Moonlight and Shadow'],
          10: ['Hidden Paths'],
          14: ['Walker in Dreams']
        }
      },
      'Circle of the Shepherd': {
        description: 'Commune with the spirits of nature. You can summon nature spirits to aid you and your allies.',
        features: {
          2: ['Speech of the Woods', 'Spirit Totem'],
          6: ['Mighty Summoner'],
          10: ['Guardian Spirit'],
          14: ['Faithful Summons']
        }
      }
    }
  },
  'Fighter': {
    hitDice: '1d10',
    subclassLevel: 3,
    startingGold: '5d4',
    primaryAbility: 'Strength or Dexterity',
    savingThrows: ['str', 'con'],
    proficiencies: {
      armor: ['All armor', 'Shields'],
      weapons: ['Simple weapons', 'Martial weapons'],
      tools: [],
      skills: {
        number: 2,
        options: ['Acrobatics', 'Animal Handling', 'Athletics', 'History', 'Insight', 'Intimidation', 'Perception', 'Survival']
      }
    },
    startingEquipment: [
      { choice: ['Chain mail', '(Leather armor, longbow, and 20 arrows)'] },
      { choice: ['(Martial weapon and shield)', 'Two martial weapons'] },
      { choice: ['Light crossbow and 20 bolts', 'Two handaxes'] },
      { choice: ['Dungeoneer\'s pack', 'Explorer\'s pack'] }
    ],
    features: {
      1: ['Fighting Style', 'Second Wind'],
      2: ['Action Surge (one use)'],
      3: ['Martial Archetype'],
      4: ['Ability Score Improvement'],
      5: ['Extra Attack'],
      6: ['Ability Score Improvement'],
      7: ['Archetype feature'],
      8: ['Ability Score Improvement'],
      9: ['Indomitable (one use)'],
      10: ['Archetype feature'],
      11: ['Extra Attack (2)'],
      12: ['Ability Score Improvement'],
      13: ['Indomitable (two uses)'],
      14: ['Ability Score Improvement'],
      15: ['Archetype feature'],
      16: ['Ability Score Improvement'],
      17: ['Action Surge (two uses)', 'Indomitable (three uses)'],
      18: ['Archetype feature'],
      19: ['Ability Score Improvement'],
      20: ['Extra Attack (3)']
    },
    // [PURPOSE]: D&D 5e SRD Fighter Martial Archetypes with features by level
    // [WHY]: Enables subclass selection at level 3 in LevelUpWizard
    // [INTEGRATION]: Used by LevelUpWizard Step 5 to display subclass options
    subclasses: {
      'Champion': {
        description: 'Focuses on the development of raw physical power honed to deadly perfection. Combines rigorous training with physical excellence.',
        features: {
          3: ['Improved Critical'],
          7: ['Remarkable Athlete'],
          10: ['Additional Fighting Style'],
          15: ['Superior Critical'],
          18: ['Survivor']
        }
      },
      'Battle Master': {
        description: 'Employs martial techniques passed down through generations. Excels in using combat maneuvers and battlefield tactics.',
        maneuverChoices: { 3: 3, 7: 2, 10: 2, 15: 2 }, // Number of maneuvers to choose at each level
        superiorityDice: { 3: { count: 4, size: 'd8' }, 7: { count: 5, size: 'd8' }, 10: { count: 5, size: 'd10' }, 15: { count: 6, size: 'd10' }, 18: { count: 6, size: 'd12' } },
        features: {
          3: ['Combat Superiority', 'Student of War'],
          7: ['Know Your Enemy'],
          10: ['Improved Combat Superiority'],
          15: ['Relentless'],
          18: ['Improved Combat Superiority']
        }
      },
      'Eldritch Knight': {
        description: 'Combines martial prowess with magical ability. Learns spells primarily from the abjuration and evocation schools.',
        spellcasting: {
          ability: 'int',
          cantrips: { 3: 2, 10: 3 },
          spellsKnown: { 3: 3, 4: 4, 7: 5, 8: 6, 10: 7, 11: 8, 13: 9, 14: 10, 16: 11, 19: 12, 20: 13 },
          restriction: 'Abjuration or Evocation (except at 3rd, 8th, 14th, 20th level)',
          spellSlots: 'One-third caster progression'
        },
        features: {
          3: ['Spellcasting', 'Weapon Bond'],
          7: ['War Magic'],
          10: ['Eldritch Strike'],
          15: ['Arcane Charge'],
          18: ['Improved War Magic']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Psi Warrior': {
        description: 'Augment your physical might with psi-infused weapon strikes, telekinetic lashes, and barriers of mental force.',
        features: {
          3: ['Psionic Power'],
          7: ['Telekinetic Adept'],
          10: ['Guarded Mind'],
          15: ['Bulwark of Force'],
          18: ['Telekinetic Master']
        }
      },
      'Rune Knight': {
        description: 'Enhance your martial prowess using the supernatural power of runes, an ancient practice from giant lore.',
        features: {
          3: ['Bonus Proficiencies', 'Rune Carver', 'Giant\'s Might'],
          7: ['Runic Shield'],
          10: ['Great Stature'],
          15: ['Master of Runes'],
          18: ['Runic Juggernaut']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Arcane Archer': {
        description: 'Employ magic to learn an assortment of supernatural arrows that can seek foes, explode, or distract enemies.',
        features: {
          3: ['Arcane Archer Lore', 'Arcane Shot'],
          7: ['Magic Arrow', 'Curving Shot'],
          10: ['Arcane Shot (2 options)'],
          15: ['Ever-Ready Shot'],
          18: ['Arcane Shot (3 options)']
        }
      },
      'Cavalier': {
        description: 'Emulate the ideals of a mounted knight. You excel at mounted combat and protecting allies.',
        features: {
          3: ['Bonus Proficiency', 'Born to the Saddle', 'Unwavering Mark'],
          7: ['Warding Maneuver'],
          10: ['Hold the Line'],
          15: ['Ferocious Charger'],
          18: ['Vigilant Defender']
        }
      },
      'Samurai': {
        description: 'Combine resilience with courtly elegance. You can push through any hardship with supernatural focus.',
        features: {
          3: ['Bonus Proficiency', 'Fighting Spirit'],
          7: ['Elegant Courtier'],
          10: ['Tireless Spirit'],
          15: ['Rapid Strike'],
          18: ['Strength Before Death']
        }
      }
    }
  },
  'Monk': {
    hitDice: '1d8',
    subclassLevel: 3,
    startingGold: '5d4',
    unarmoredDefense: { abilities: ['dexterity', 'wisdom'], noShield: true },
    primaryAbility: 'Dexterity and Wisdom',
    savingThrows: ['str', 'dex'],
    proficiencies: {
      armor: [],
      weapons: ['Simple weapons', 'Shortswords'],
      tools: ['One type of artisan\'s tools or one musical instrument'],
      skills: {
        number: 2,
        options: ['Acrobatics', 'Athletics', 'History', 'Insight', 'Religion', 'Stealth']
      }
    },
    startingEquipment: [
      { choice: ['Shortsword', 'Any simple weapon'] },
      { choice: ['Dungeoneer\'s pack', 'Explorer\'s pack'] },
      '10 darts'
    ],
    features: {
      1: ['Unarmored Defense', 'Martial Arts'],
      2: ['Ki', 'Unarmored Movement'],
      3: ['Monastic Tradition', 'Deflect Missiles'],
      4: ['Ability Score Improvement', 'Slow Fall'],
      5: ['Extra Attack', 'Stunning Strike'],
      6: ['Ki-Empowered Strikes', 'Monastic Tradition feature'],
      7: ['Evasion', 'Stillness of Mind'],
      8: ['Ability Score Improvement'],
      9: ['Unarmored Movement improvement'],
      10: ['Purity of Body'],
      11: ['Monastic Tradition feature'],
      12: ['Ability Score Improvement'],
      13: ['Tongue of the Sun and Moon'],
      14: ['Diamond Soul'],
      15: ['Timeless Body'],
      16: ['Ability Score Improvement'],
      17: ['Monastic Tradition feature'],
      18: ['Empty Body'],
      19: ['Ability Score Improvement'],
      20: ['Perfect Self']
    },
    // [PURPOSE]: D&D 5e SRD Monk Monastic Traditions
    // [WHY]: Monks choose their tradition at level 3
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'Way of the Open Hand': {
        description: 'Masters of unarmed combat techniques. You can manipulate your enemy\'s ki to cause debilitating effects and heal yourself.',
        features: {
          3: ['Open Hand Technique'],
          6: ['Wholeness of Body'],
          11: ['Tranquility'],
          17: ['Quivering Palm']
        }
      },
      'Way of Shadow': {
        description: 'Ninja-like warriors who use stealth and illusion. You can teleport through shadows and cast shadow magic.',
        shadowSpells: { 3: ['Minor Illusion', 'Darkness', 'Darkvision', 'Pass without Trace', 'Silence'] },
        features: {
          3: ['Shadow Arts'],
          6: ['Shadow Step'],
          11: ['Cloak of Shadows'],
          17: ['Opportunist']
        }
      },
      'Way of the Four Elements': {
        description: 'Harness the elements through your ki. You learn to cast elemental spells and channel destructive forces.',
        disciplineChoices: { 3: 2, 6: 1, 11: 1, 17: 1 }, // Number of disciplines to choose at each level
        disciplineOptions: [
          'Fangs of the Fire Snake', 'Fist of Four Thunders', 'Fist of Unbroken Air', 'Rush of the Gale Spirits',
          'Shape the Flowing River', 'Sweeping Cinder Strike', 'Water Whip', 'Clench of the North Wind',
          'Gong of the Summit', 'Flames of the Phoenix', 'Mist Stance', 'Ride the Wind', 'River of Hungry Flame',
          'Breath of Winter', 'Eternal Mountain Defense', 'Wave of Rolling Earth'
        ],
        features: {
          3: ['Disciple of the Elements', 'Elemental Attunement'],
          6: ['Extra Elemental Discipline'],
          11: ['Extra Elemental Discipline'],
          17: ['Extra Elemental Discipline']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Way of Mercy': {
        description: 'Learn to manipulate the life force of others to bring aid to those in need and harm to your foes.',
        features: {
          3: ['Implements of Mercy', 'Hand of Healing', 'Hand of Harm'],
          6: ['Physician\'s Touch'],
          11: ['Flurry of Healing and Harm'],
          17: ['Hand of Ultimate Mercy']
        }
      },
      'Way of the Astral Self': {
        description: 'Project your ki to form a spectral representation of your inner self. This astral self aids you in combat.',
        features: {
          3: ['Arms of the Astral Self'],
          6: ['Visage of the Astral Self'],
          11: ['Body of the Astral Self'],
          17: ['Awakening of the Astral Self']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Way of the Drunken Master': {
        description: 'Learn to move with the jerky, unpredictable movements of a drunkard. You sway, bob, and weave to evade attacks.',
        features: {
          3: ['Bonus Proficiencies', 'Drunken Technique'],
          6: ['Tipsy Sway'],
          11: ['Drunkard\'s Luck'],
          17: ['Intoxicated Frenzy']
        }
      },
      'Way of the Kensei': {
        description: 'Train relentlessly with weapons, to the point they become extensions of your body. You turn weapons into monk weapons.',
        features: {
          3: ['Path of the Kensei', 'Kensei Weapons'],
          6: ['One with the Blade'],
          11: ['Sharpen the Blade'],
          17: ['Unerring Accuracy']
        }
      },
      'Way of the Sun Soul': {
        description: 'Learn to channel your own life energy into searing bolts of light. You hurl magic projectiles and create devastating bursts.',
        features: {
          3: ['Radiant Sun Bolt'],
          6: ['Searing Arc Strike'],
          11: ['Searing Sunburst'],
          17: ['Sun Shield']
        }
      }
    }
  },
  'Paladin': {
    hitDice: '1d10',
    subclassLevel: 3,
    startingGold: '5d4',
    primaryAbility: 'Strength and Charisma',
    savingThrows: ['wis', 'cha'],
    // [PHASE B / #036 — 2026-05-06]: PartyPanel auras data block.
    //   Resolves at render time via auraResolver. Pre-#036 these were
    //   hardcoded `className === 'Paladin'` blocks in PartyPanel.js.
    auras: [
      {
        name: 'Aura of Protection',
        minLevel: 6,
        radius: { byLevel: [{ minLevel: 6, value: 10 }, { minLevel: 18, value: 30 }] },
        bonus: { fromAbility: 'charisma', floor: 1 },
        descriptionTemplate: '+${bonus} to saves for self and friendly creatures within ${radius} ft. (PHB p.85)',
        color: 'bg-amber-900/30 border-amber-700/40 text-amber-200',
      },
      {
        name: 'Aura of Courage',
        minLevel: 10,
        radius: { byLevel: [{ minLevel: 10, value: 10 }, { minLevel: 18, value: 30 }] },
        descriptionTemplate: 'Friendly creatures within ${radius} ft cannot be frightened while you are conscious. (PHB p.85)',
        color: 'bg-yellow-900/30 border-yellow-700/40 text-yellow-200',
      },
    ],
    proficiencies: {
      armor: ['All armor', 'Shields'],
      weapons: ['Simple weapons', 'Martial weapons'],
      tools: [],
      skills: {
        number: 2,
        options: ['Athletics', 'Insight', 'Intimidation', 'Medicine', 'Persuasion', 'Religion']
      }
    },
    spellcasting: {
      ability: 'cha',
      casterType: 'half',
      startLevel: 2,
      preparedSpells: 'Charisma modifier + half paladin level'
    },
    startingEquipment: [
      { choice: ['(Martial weapon and shield)', 'Two martial weapons'] },
      { choice: ['Five javelins', 'Any simple melee weapon'] },
      { choice: ['Priest\'s pack', 'Explorer\'s pack'] },
      'Chain mail',
      'Holy symbol'
    ],
    features: {
      1: ['Divine Sense', 'Lay on Hands'],
      2: ['Fighting Style', 'Spellcasting', 'Divine Smite'],
      3: ['Divine Health', 'Sacred Oath'],
      4: ['Ability Score Improvement'],
      5: ['Extra Attack'],
      6: ['Aura of Protection'],
      7: ['Oath feature'],
      8: ['Ability Score Improvement'],
      9: [],
      10: ['Aura of Courage'],
      11: ['Improved Divine Smite'],
      12: ['Ability Score Improvement'],
      13: [],
      14: ['Cleansing Touch'],
      15: ['Oath feature'],
      16: ['Ability Score Improvement'],
      17: [],
      18: ['Aura improvements'],
      19: ['Ability Score Improvement'],
      20: ['Oath feature']
    },
    // [PURPOSE]: D&D 5e SRD Paladin Sacred Oaths
    // [WHY]: Paladins choose their oath at level 3
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'Oath of Devotion': {
        description: 'Uphold the loftiest ideals of justice and virtue. You are a beacon of hope in dark times, committed to fighting evil wherever it lurks.',
        oathSpells: {
          3: ['Protection from Evil and Good', 'Sanctuary'],
          5: ['Lesser Restoration', 'Zone of Truth'],
          9: ['Beacon of Hope', 'Dispel Magic'],
          13: ['Freedom of Movement', 'Guardian of Faith'],
          17: ['Commune', 'Flame Strike']
        },
        features: {
          3: ['Channel Divinity: Sacred Weapon', 'Channel Divinity: Turn the Unholy'],
          7: ['Aura of Devotion'],
          15: ['Purity of Spirit'],
          20: ['Holy Nimbus']
        }
      },
      'Oath of the Ancients': {
        description: 'Preserve the light and beauty of the world. You are a knight of nature, standing against the forces of darkness and decay.',
        oathSpells: {
          3: ['Ensnaring Strike', 'Speak with Animals'],
          5: ['Moonbeam', 'Misty Step'],
          9: ['Plant Growth', 'Protection from Energy'],
          13: ['Ice Storm', 'Stoneskin'],
          17: ['Commune with Nature', 'Tree Stride']
        },
        features: {
          3: ['Channel Divinity: Nature\'s Wrath', 'Channel Divinity: Turn the Faithless'],
          7: ['Aura of Warding'],
          15: ['Undying Sentinel'],
          20: ['Elder Champion']
        }
      },
      'Oath of Vengeance': {
        description: 'Punish wrongdoers with swift and terrible retribution. You are an avenging angel, dedicated to hunting down those who do great evil.',
        // [PHASE B / #023 — 2026-05-06]: Combat-toggle ownership.
        //   Vow of Enmity is a Channel Divinity option granted at L3
        //   alongside the subclass; the toggle exposes a per-target
        //   "this is my vowed target" flag without level gating.
        combatToggles: [
          { flag: 'attackingVowedTarget' },
        ],
        oathSpells: {
          3: ['Bane', 'Hunter\'s Mark'],
          5: ['Hold Person', 'Misty Step'],
          9: ['Haste', 'Protection from Energy'],
          13: ['Banishment', 'Dimension Door'],
          17: ['Hold Monster', 'Scrying']
        },
        features: {
          3: ['Channel Divinity: Abjure Enemy', 'Channel Divinity: Vow of Enmity'],
          7: ['Relentless Avenger'],
          15: ['Soul of Vengeance'],
          20: ['Avenging Angel']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Oath of Glory': {
        description: 'Believe that you and your friends are destined for glory through deeds of heroism. You train diligently to be legend in the making.',
        oathSpells: {
          3: ['Guiding Bolt', 'Heroism'],
          5: ['Enhance Ability', 'Magic Weapon'],
          9: ['Haste', 'Protection from Energy'],
          13: ['Compulsion', 'Freedom of Movement'],
          17: ['Commune', 'Flame Strike']
        },
        features: {
          3: ['Channel Divinity: Peerless Athlete', 'Channel Divinity: Inspiring Smite'],
          7: ['Aura of Alacrity'],
          15: ['Glorious Defense'],
          20: ['Living Legend']
        }
      },
      'Oath of the Watchers': {
        description: 'Sworn to protect the mortal world from extraplanar threats. You stand vigilant against fiends, aberrations, and fey.',
        oathSpells: {
          3: ['Alarm', 'Detect Magic'],
          5: ['Moonbeam', 'See Invisibility'],
          9: ['Counterspell', 'Nondetection'],
          13: ['Aura of Purity', 'Banishment'],
          17: ['Hold Monster', 'Scrying']
        },
        features: {
          3: ['Channel Divinity: Watcher\'s Will', 'Channel Divinity: Abjure the Extraplanar'],
          7: ['Aura of the Sentinel'],
          15: ['Vigilant Rebuke'],
          20: ['Mortal Bulwark']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Oath of Conquest': {
        description: 'Seek glory in battle and the subjugation of your enemies. You bring despair to your foes and inspire fear in all who oppose you.',
        oathSpells: {
          3: ['Armor of Agathys', 'Command'],
          5: ['Hold Person', 'Spiritual Weapon'],
          9: ['Bestow Curse', 'Fear'],
          13: ['Dominate Beast', 'Stoneskin'],
          17: ['Cloudkill', 'Dominate Person']
        },
        features: {
          3: ['Channel Divinity: Conquering Presence', 'Channel Divinity: Guided Strike'],
          7: ['Aura of Conquest'],
          15: ['Scornful Rebuke'],
          20: ['Invincible Conqueror']
        }
      },
      'Oath of Redemption': {
        description: 'Believe that anyone can be redeemed. You use violence only as a last resort, preferring to deflect attacks and protect the weak.',
        oathSpells: {
          3: ['Sanctuary', 'Sleep'],
          5: ['Calm Emotions', 'Hold Person'],
          9: ['Counterspell', 'Hypnotic Pattern'],
          13: ['Otiluke\'s Resilient Sphere', 'Stoneskin'],
          17: ['Hold Monster', 'Wall of Force']
        },
        features: {
          3: ['Channel Divinity: Emissary of Peace', 'Channel Divinity: Rebuke the Violent'],
          7: ['Aura of the Guardian'],
          15: ['Protective Spirit'],
          20: ['Emissary of Redemption']
        }
      }
    }
  },
  'Ranger': {
    hitDice: '1d10',
    subclassLevel: 3,
    startingGold: '5d4',
    primaryAbility: 'Dexterity and Wisdom',
    savingThrows: ['str', 'dex'],
    // [PHASE B / #022 — 2026-05-06]: Combat-toggle ownership data block.
    //   Hunter's Mark is on Ranger's base spell list; Favored Enemy is
    //   the L1 Ranger feature. Both flags surface across all subclasses.
    combatToggles: [
      { flag: 'huntersMarkTargetActive' },
      { flag: 'attackingFavoredEnemy' },
    ],
    // [PHASE B / #046 — MAY 2026]: Wizard picker step. PHB p.91 —
    //   Ranger L1 Favored Enemy: pick a creature type. Pre-fix the
    //   trigger lived as a hardcoded `className === 'Ranger'` branch
    //   in LevelUpWizard.js. Step fires whenever a Ranger L1+ has
    //   not yet committed `playerStats.favoredEnemy`.
    levelProgression: [
      {
        atLevel: 1,
        action: 'wizardPickerStep',
        stepId: 'favored_enemy',
        title: 'Favored Enemy',
        icon: 'Scroll',
        completedKey: 'favoredEnemy',
      },
    ],
    proficiencies: {
      armor: ['Light armor', 'Medium armor', 'Shields'],
      weapons: ['Simple weapons', 'Martial weapons'],
      tools: [],
      skills: {
        number: 3,
        options: ['Animal Handling', 'Athletics', 'Insight', 'Investigation', 'Nature', 'Perception', 'Stealth', 'Survival']
      }
    },
    spellcasting: {
      ability: 'wis',
      casterType: 'half',
      startLevel: 2,
      spellsKnown: { 2: 2, 3: 3, 5: 4, 7: 5, 9: 6, 11: 7, 13: 8, 15: 9, 17: 10, 19: 11 }
    },
    startingEquipment: [
      { choice: ['Scale mail', 'Leather armor'] },
      { choice: ['Two shortswords', 'Two simple melee weapons'] },
      { choice: ['Dungeoneer\'s pack', 'Explorer\'s pack'] },
      'Longbow',
      'Quiver of 20 arrows'
    ],
    features: {
      1: ['Favored Enemy', 'Natural Explorer'],
      2: ['Fighting Style', 'Spellcasting'],
      3: ['Ranger Archetype', 'Primeval Awareness'],
      4: ['Ability Score Improvement'],
      5: ['Extra Attack'],
      6: ['Favored Enemy improvement', 'Natural Explorer improvement'],
      7: ['Archetype feature'],
      8: ['Ability Score Improvement', 'Land\'s Stride'],
      9: [],
      10: ['Natural Explorer improvement', 'Hide in Plain Sight'],
      11: ['Archetype feature'],
      12: ['Ability Score Improvement'],
      13: [],
      14: ['Favored Enemy improvement', 'Vanish'],
      15: ['Archetype feature'],
      16: ['Ability Score Improvement'],
      17: [],
      18: ['Feral Senses'],
      19: ['Ability Score Improvement'],
      20: ['Foe Slayer']
    },
    // [PURPOSE]: D&D 5e SRD Ranger Archetypes
    // [WHY]: Rangers choose their archetype at level 3
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'Hunter': {
        description: 'Master of tracking and slaying specific types of foes. You learn specialized combat techniques to defeat your enemies.',
        hunterChoices: {
          3: { name: 'Hunter\'s Prey', options: ['Colossus Slayer', 'Giant Killer', 'Horde Breaker'] },
          7: { name: 'Defensive Tactics', options: ['Escape the Horde', 'Multiattack Defense', 'Steel Will'] },
          11: { name: 'Multiattack', options: ['Volley', 'Whirlwind Attack'] },
          15: { name: 'Superior Hunter\'s Defense', options: ['Evasion', 'Stand Against the Tide', 'Uncanny Dodge'] }
        },
        features: {
          3: ['Hunter\'s Prey'],
          7: ['Defensive Tactics'],
          11: ['Multiattack'],
          15: ['Superior Hunter\'s Defense']
        }
      },
      'Beast Master': {
        description: 'Form a bond with a beast companion that fights alongside you. Your animal ally grows in power as you do.',
        features: {
          3: ['Ranger\'s Companion'],
          7: ['Exceptional Training'],
          11: ['Bestial Fury'],
          15: ['Share Spells']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Fey Wanderer': {
        description: 'Walk the line between the Feywild and the Material Plane. You possess a mysterious, otherworldly presence.',
        features: {
          3: ['Dreadful Strikes', 'Fey Wanderer Magic', 'Otherworldly Glamour'],
          7: ['Beguiling Twist'],
          11: ['Fey Reinforcements'],
          15: ['Misty Wanderer']
        }
      },
      'Swarmkeeper': {
        description: 'Bonded to a swarm of nature spirits. These spirits help you in battle and movement, gathering around you like a cloak.',
        features: {
          3: ['Gathered Swarm', 'Swarmkeeper Magic'],
          7: ['Writhing Tide'],
          11: ['Mighty Swarm'],
          15: ['Swarming Dispersal']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Gloom Stalker': {
        description: 'At home in the darkest places. You ambush enemies from the shadows, moving with supernatural stealth.',
        features: {
          3: ['Dread Ambusher', 'Umbral Sight', 'Gloom Stalker Magic'],
          7: ['Iron Mind'],
          11: ['Stalker\'s Flurry'],
          15: ['Shadowy Dodge']
        }
      },
      'Horizon Walker': {
        description: 'Guard the world against threats from other planes. You learn to teleport and strike foes from multiple planes at once.',
        features: {
          3: ['Detect Portal', 'Planar Warrior', 'Horizon Walker Magic'],
          7: ['Ethereal Step'],
          11: ['Distant Strike'],
          15: ['Spectral Defense']
        }
      },
      'Monster Slayer': {
        description: 'Specialize in hunting down creatures of evil. You learn supernatural defenses and can turn enemies\' strengths against them.',
        features: {
          3: ['Hunter\'s Sense', 'Slayer\'s Prey', 'Monster Slayer Magic'],
          7: ['Supernatural Defense'],
          11: ['Magic-User\'s Nemesis'],
          15: ['Slayer\'s Counter']
        }
      }
    }
  },
  'Rogue': {
    hitDice: '1d8',
    subclassLevel: 3,
    startingGold: '4d4',
    primaryAbility: 'Dexterity',
    savingThrows: ['dex', 'int'],
    // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
    levelProgression: [
      { atLevel: 1, action: 'expertisePicks', count: 2 },
      { atLevel: 6, action: 'expertisePicks', count: 2 },
    ],
    // [PHASE B / #022 — 2026-05-06]: Combat-toggle ownership data block.
    //   Sneak Attack is the L1 Rogue feature; toggle is class-wide.
    combatToggles: [
      { flag: 'sneakAttackActive' },
    ],
    proficiencies: {
      armor: ['Light armor'],
      weapons: ['Simple weapons', 'Hand crossbows', 'Longswords', 'Rapiers', 'Shortswords'],
      tools: ['Thieves\' tools'],
      skills: {
        number: 4,
        options: ['Acrobatics', 'Athletics', 'Deception', 'Insight', 'Intimidation', 'Investigation', 'Perception', 'Performance', 'Persuasion', 'Sleight of Hand', 'Stealth']
      }
    },
    startingEquipment: [
      { choice: ['Rapier', 'Shortsword'] },
      { choice: ['(Shortbow and quiver of 20 arrows)', 'Shortsword'] },
      { choice: ['Burglar\'s pack', 'Dungeoneer\'s pack', 'Explorer\'s pack'] },
      'Leather armor',
      'Two daggers',
      'Thieves\' tools'
    ],
    features: {
      1: ['Expertise', 'Sneak Attack (1d6)', 'Thieves\' Cant'],
      2: ['Cunning Action'],
      3: ['Roguish Archetype', 'Sneak Attack (2d6)'],
      4: ['Ability Score Improvement'],
      5: ['Uncanny Dodge', 'Sneak Attack (3d6)'],
      6: ['Expertise'],
      7: ['Evasion', 'Sneak Attack (4d6)'],
      8: ['Ability Score Improvement'],
      9: ['Archetype feature', 'Sneak Attack (5d6)'],
      10: ['Ability Score Improvement'],
      11: ['Reliable Talent', 'Sneak Attack (6d6)'],
      12: ['Ability Score Improvement'],
      13: ['Archetype feature', 'Sneak Attack (7d6)'],
      14: ['Blindsense'],
      15: ['Slippery Mind', 'Sneak Attack (8d6)'],
      16: ['Ability Score Improvement'],
      17: ['Archetype feature', 'Sneak Attack (9d6)'],
      18: ['Elusive'],
      19: ['Ability Score Improvement', 'Sneak Attack (10d6)'],
      20: ['Stroke of Luck']
    },
    // [PURPOSE]: D&D 5e SRD Rogue Roguish Archetypes
    // [WHY]: Rogues choose their archetype at level 3
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'Thief': {
        description: 'Master of stealth and infiltration. You have exceptional agility and can use objects with incredible speed.',
        features: {
          3: ['Fast Hands', 'Second-Story Work'],
          9: ['Supreme Sneak'],
          13: ['Use Magic Device'],
          17: ['Thief\'s Reflexes']
        }
      },
      'Assassin': {
        description: 'Master of dealing sudden death. You excel at eliminating targets quickly and disguising your identity.',
        // [PHASE B / #023 — 2026-05-06]: Combat-toggle ownership.
        //   Assassinate (L3) gives any hit on a surprised creature an
        //   automatic crit; the toggle exposes the surprise state.
        combatToggles: [
          { flag: 'attackingSurprisedTarget' },
        ],
        features: {
          3: ['Bonus Proficiencies', 'Assassinate'],
          9: ['Infiltration Expertise'],
          13: ['Impostor'],
          17: ['Death Strike']
        }
      },
      'Arcane Trickster': {
        description: 'Blend rogue skills with enchantment and illusion magic. You use spells to enhance your stealth and trickery.',
        spellcasting: {
          ability: 'int',
          cantrips: { 3: 3, 10: 4 },
          spellsKnown: { 3: 3, 4: 4, 7: 5, 8: 6, 10: 7, 11: 8, 13: 9, 14: 10, 16: 11, 19: 12, 20: 13 },
          restriction: 'Enchantment or Illusion from wizard list (except at 3rd, 8th, 14th, 20th level)',
          spellSlots: 'One-third caster progression'
        },
        features: {
          3: ['Spellcasting', 'Mage Hand Legerdemain'],
          9: ['Magical Ambush'],
          13: ['Versatile Trickster'],
          17: ['Spell Thief']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Phantom': {
        description: 'Walk the line between life and death. You gain the ability to communicate with the dead and channel necrotic energy.',
        features: {
          3: ['Whispers of the Dead', 'Wails from the Grave'],
          9: ['Tokens of the Departed'],
          13: ['Ghost Walk'],
          17: ['Death\'s Friend']
        }
      },
      'Soulknife': {
        description: 'Strike with blades formed from your own psychic energy. You can telepathically communicate and use psionic powers.',
        features: {
          3: ['Psionic Power', 'Psychic Blades'],
          9: ['Soul Blades'],
          13: ['Psychic Veil'],
          17: ['Rend Mind']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Inquisitive': {
        description: 'Excel at rooting out secrets and unraveling mysteries. Your keen eye for detail makes you an exceptional investigator.',
        features: {
          3: ['Ear for Deceit', 'Eye for Detail', 'Insightful Fighting'],
          9: ['Steady Eye'],
          13: ['Unerring Eye'],
          17: ['Eye for Weakness']
        }
      },
      'Mastermind': {
        description: 'Master of intrigue. You excel at deception, manipulation, and gathering intelligence.',
        features: {
          3: ['Master of Intrigue', 'Master of Tactics'],
          9: ['Insightful Manipulator'],
          13: ['Misdirection'],
          17: ['Soul of Deceit']
        }
      },
      'Scout': {
        description: 'Skilled hunter and tracker. You excel at hit-and-run tactics and can move quickly through difficult terrain.',
        features: {
          3: ['Skirmisher', 'Survivalist'],
          9: ['Superior Mobility'],
          13: ['Ambush Master'],
          17: ['Sudden Strike']
        }
      },
      'Swashbuckler': {
        description: 'Charming combatant who excels in one-on-one combat. You blend speed, elegance, and charm into a deadly fighting style.',
        features: {
          3: ['Fancy Footwork', 'Rakish Audacity'],
          9: ['Panache'],
          13: ['Elegant Maneuver'],
          17: ['Master Duelist']
        }
      }
    }
  },
  'Sorcerer': {
    hitDice: '1d6',
    subclassLevel: 1,
    startingGold: '3d4',
    primaryAbility: 'Charisma',
    savingThrows: ['con', 'cha'],
    // [#97 2026-05-19]: Help-text moved off CCW string-equality gates onto class data.
    subclassChoiceTitle: 'Sorcerous Origin',
    subclassChoiceHelp: 'Your sorcerous origin is the source of your magic and grants origin-specific features starting at level 1.',
    spellsSourceHelp: ' You also gain sorcery points to enhance your spells as you level.',
    spellSuggestionTip: ' Versatile spells like Shield and Magic Missile are great choices.',
    // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
    //   PHB p.101: 2 metamagic at L3, +1 at L10, +1 at L17.
    levelProgression: [
      { atLevel: 3,  action: 'metamagicPicks', count: 2 },
      { atLevel: 10, action: 'metamagicPicks', count: 1 },
      { atLevel: 17, action: 'metamagicPicks', count: 1 },
    ],
    proficiencies: {
      armor: [],
      weapons: ['Daggers', 'Darts', 'Slings', 'Quarterstaffs', 'Light crossbows'],
      tools: [],
      skills: {
        number: 2,
        options: ['Arcana', 'Deception', 'Insight', 'Intimidation', 'Persuasion', 'Religion']
      }
    },
    spellcasting: {
      ability: 'cha',
      casterType: 'full',
      cantrips: { 1: 4, 4: 5, 10: 6 },
      spellsKnown: { 1: 2, 2: 3, 3: 4, 4: 5, 5: 6, 6: 7, 7: 8, 8: 9, 9: 10, 10: 11, 11: 12, 13: 13, 15: 14, 17: 15 }
    },
    startingEquipment: [
      { choice: ['Light crossbow and 20 bolts', 'Any simple weapon'] },
      { choice: ['Component pouch', 'Arcane focus'] },
      { choice: ['Dungeoneer\'s pack', 'Explorer\'s pack'] },
      'Two daggers'
    ],
    features: {
      1: ['Spellcasting', 'Sorcerous Origin'],
      2: ['Font of Magic'],
      3: ['Metamagic'],
      4: ['Ability Score Improvement'],
      5: [],
      6: ['Origin feature'],
      7: [],
      8: ['Ability Score Improvement'],
      9: [],
      10: ['Metamagic'],
      11: [],
      12: ['Ability Score Improvement'],
      13: [],
      14: ['Origin feature'],
      15: [],
      16: ['Ability Score Improvement'],
      17: ['Metamagic'],
      18: ['Origin feature'],
      19: ['Ability Score Improvement'],
      20: ['Sorcerous Restoration']
    },
    // [PURPOSE]: D&D 5e SRD Sorcerer Sorcerous Origins
    // [WHY]: Sorcerers choose their origin at level 1
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'Draconic Bloodline': {
        description: 'Your innate magic comes from draconic heritage. You have a dragon ancestor whose power flows through your veins.',
        // [PHASE B / #023 — 2026-05-06]: Combat-toggle ownership.
        //   Elemental Affinity (L6) adds CHA mod damage to spells matching
        //   your dragon ancestor element; the toggle marks the casting.
        combatToggles: [
          { flag: 'castingDraconicElementSpell', minLevel: 6 },
        ],
        dragonAncestorChoice: { 1: 1 }, // Choose dragon ancestor at level 1
        dragonOptions: [
          { type: 'Black', damageType: 'Acid' },
          { type: 'Blue', damageType: 'Lightning' },
          { type: 'Brass', damageType: 'Fire' },
          { type: 'Bronze', damageType: 'Lightning' },
          { type: 'Copper', damageType: 'Acid' },
          { type: 'Gold', damageType: 'Fire' },
          { type: 'Green', damageType: 'Poison' },
          { type: 'Red', damageType: 'Fire' },
          { type: 'Silver', damageType: 'Cold' },
          { type: 'White', damageType: 'Cold' }
        ],
        features: {
          1: ['Dragon Ancestor', 'Draconic Resilience'],
          6: ['Elemental Affinity'],
          14: ['Dragon Wings'],
          18: ['Draconic Presence']
        }
      },
      'Wild Magic': {
        description: 'Your innate magic stems from the wild forces of chaos. Your spells can trigger unpredictable magical effects.',
        wildMagicSurge: true, // Reference to Wild Magic Surge table
        features: {
          1: ['Wild Magic Surge', 'Tides of Chaos'],
          6: ['Bend Luck'],
          14: ['Controlled Chaos'],
          18: ['Spell Bombardment']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'Aberrant Mind': {
        description: 'Your innate magic comes from an alien influence. You wield psionic power and can reshape your spells with psionic energy.',
        features: {
          1: ['Psionic Spells', 'Telepathic Speech'],
          6: ['Psionic Sorcery', 'Psychic Defenses'],
          14: ['Revelation in Flesh'],
          18: ['Warping Implosion']
        }
      },
      'Clockwork Soul': {
        description: 'Your magic springs from the plane of Mechanus. You can channel the cosmic force of order to restore balance.',
        features: {
          1: ['Clockwork Magic', 'Restore Balance'],
          6: ['Bastion of Law'],
          14: ['Trance of Order'],
          18: ['Clockwork Cavalcade']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'Divine Soul': {
        description: 'Your innate magic comes from a divine source. You can channel both arcane and divine magic.',
        features: {
          1: ['Divine Magic', 'Favored by the Gods'],
          6: ['Empowered Healing'],
          14: ['Otherworldly Wings'],
          18: ['Unearthly Recovery']
        }
      },
      'Shadow Magic': {
        description: 'Your innate magic comes from the Shadowfell. You can manipulate shadows and darkness, and even cheat death.',
        features: {
          1: ['Eyes of the Dark', 'Strength of the Grave'],
          6: ['Hound of Ill Omen'],
          14: ['Shadow Walk'],
          18: ['Umbral Form']
        }
      },
      'Storm Sorcery': {
        description: 'Your innate magic comes from the power of elemental air. You can fly on the winds and call upon storms.',
        features: {
          1: ['Wind Speaker', 'Tempestuous Magic'],
          6: ['Heart of the Storm', 'Storm Guide'],
          14: ['Storm\'s Fury'],
          18: ['Wind Soul']
        }
      }
    }
  },
  'Warlock': {
    hitDice: '1d8',
    subclassLevel: 1,
    startingGold: '4d4',
    primaryAbility: 'Charisma',
    savingThrows: ['wis', 'cha'],
    // [#97 2026-05-19]: Help-text moved off CCW string-equality gates onto class data.
    subclassChoiceTitle: 'Otherworldly Patron',
    subclassChoiceHelp: 'Your patron is the being that granted you power and shapes your pact features starting at level 1.',
    spellsSourceHelp: ' Your spells are cast using spell slots that refresh on a short rest.',
    spellSuggestionTip: ' Consider taking spells like Hex or Eldritch Blast for reliable damage.',
    // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
    //   PHB p.107: Pact Boon at L3 (subclass-style pick), Mystic
    //   Arcanum at L11/13/15/17 (one spell of specific level each).
    levelProgression: [
      { atLevel: 3,  action: 'subclassPick', subclassDataKey: 'pactBoon', dialogTitle: 'Pact Boon',
        headerEmoji: '🔮',
        subtitle: 'Your patron grants you a special gift. Pick one Pact Boon — this is a permanent character choice.',
        options: [
          { key: 'Pact of the Blade',    icon: '🗡️', description: 'Action: create a magic weapon in your hand. You can also bind any magic weapon as your pact weapon.' },
          { key: 'Pact of the Tome',     icon: '📖', description: 'Book of Shadows: pick 3 cantrips from any class. Cast them at-will.' },
          { key: 'Pact of the Chain',    icon: '⛓️', description: 'Familiar: imp / pseudodragon / quasit / sprite. Find Familiar spell granted.' },
          { key: 'Pact of the Talisman', icon: '🧿', description: 'TCE — Magic talisman: when wearer fails an ability check, add d4 (PB times per long rest).' },
        ],
      },
      { atLevel: 11, action: 'mysticArcanum', count: 1, dialogTitle: 'Mystic Arcanum (6th Level)', classFilter: 'Warlock', levelFilter: 6 },
      { atLevel: 13, action: 'mysticArcanum', count: 1, dialogTitle: 'Mystic Arcanum (7th Level)', classFilter: 'Warlock', levelFilter: 7 },
      { atLevel: 15, action: 'mysticArcanum', count: 1, dialogTitle: 'Mystic Arcanum (8th Level)', classFilter: 'Warlock', levelFilter: 8 },
      { atLevel: 17, action: 'mysticArcanum', count: 1, dialogTitle: 'Mystic Arcanum (9th Level)', classFilter: 'Warlock', levelFilter: 9 },
      // [PHASE B / #046 — MAY 2026]: Wizard picker step. PHB p.108 —
      //   Pact of the Tome grants 3 cantrips from any class spell list.
      //   Step fires when (a) Warlock at L3+ AND (b) chosen pact boon
      //   is Pact of the Tome AND (c) cantrips not yet committed.
      //   Pre-fix this trigger lived as a hardcoded
      //   `className === 'Warlock' && pactBoon === 'Pact of the Tome'`
      //   branch in LevelUpWizard.js.
      {
        atLevel: 3,
        action: 'wizardPickerStep',
        stepId: 'pact_tome_cantrips',
        title: 'Tome Cantrips',
        icon: 'BookOpen',
        completedKey: 'pactTomeCantrips',
        requiresChoice: { choiceKey: 'pactBoon', value: 'Pact of the Tome' },
      },
    ],
    proficiencies: {
      armor: ['Light armor'],
      weapons: ['Simple weapons'],
      tools: [],
      skills: {
        number: 2,
        options: ['Arcana', 'Deception', 'History', 'Intimidation', 'Investigation', 'Nature', 'Religion']
      }
    },
    spellcasting: {
      ability: 'cha',
      casterType: 'pact',
      cantrips: { 1: 2, 4: 3, 10: 4 },
      spellsKnown: { 1: 2, 2: 3, 3: 4, 4: 5, 5: 6, 6: 7, 7: 8, 8: 9, 9: 10, 10: 10, 11: 11, 13: 12, 15: 13, 17: 14, 19: 15 },
      pactMagic: true
    },
    startingEquipment: [
      { choice: ['Light crossbow and 20 bolts', 'Any simple weapon'] },
      { choice: ['Component pouch', 'Arcane focus'] },
      { choice: ['Scholar\'s pack', 'Dungeoneer\'s pack'] },
      'Leather armor',
      'Any simple weapon',
      'Two daggers'
    ],
    features: {
      1: ['Otherworldly Patron', 'Pact Magic'],
      2: ['Eldritch Invocations'],
      3: ['Pact Boon'],
      4: ['Ability Score Improvement'],
      5: ['Invocation'],
      6: ['Patron feature'],
      7: ['Invocation'],
      8: ['Ability Score Improvement'],
      9: ['Invocation'],
      10: ['Patron feature'],
      11: ['Mystic Arcanum (6th level)'],
      12: ['Ability Score Improvement', 'Invocation'],
      13: ['Mystic Arcanum (7th level)'],
      14: ['Patron feature'],
      15: ['Mystic Arcanum (8th level)', 'Invocation'],
      16: ['Ability Score Improvement'],
      17: ['Mystic Arcanum (9th level)'],
      18: ['Invocation'],
      19: ['Ability Score Improvement'],
      20: ['Eldritch Master']
    },
    // [PURPOSE]: D&D 5e SRD Warlock Otherworldly Patrons
    // [WHY]: Warlocks choose their patron at level 1
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'The Fiend': {
        description: 'Your patron is a powerful being from the Lower Planes. You gain power from fiendish entities and gain resistance to fire and temporary hit points.',
        expandedSpellList: {
          1: ['Burning Hands', 'Command'],
          3: ['Blindness/Deafness', 'Scorching Ray'],
          5: ['Fireball', 'Stinking Cloud'],
          7: ['Fire Shield', 'Wall of Fire'],
          9: ['Flame Strike', 'Hallow']
        },
        features: {
          1: ['Dark One\'s Blessing'],
          6: ['Dark One\'s Own Luck'],
          10: ['Fiendish Resilience'],
          14: ['Hurl Through Hell']
        }
      },
      'The Archfey': {
        description: 'Your patron is a lord or lady of the fey. You gain abilities to charm and confuse, and can vanish from sight.',
        expandedSpellList: {
          1: ['Faerie Fire', 'Sleep'],
          3: ['Calm Emotions', 'Phantasmal Force'],
          5: ['Blink', 'Plant Growth'],
          7: ['Dominate Beast', 'Greater Invisibility'],
          9: ['Dominate Person', 'Seeming']
        },
        features: {
          1: ['Fey Presence'],
          6: ['Misty Escape'],
          10: ['Beguiling Defenses'],
          14: ['Dark Delirium']
        }
      },
      'The Great Old One': {
        description: 'Your patron is an ancient entity from beyond reality. You gain telepathy and can shield your mind from intrusion.',
        expandedSpellList: {
          1: ['Dissonant Whispers', 'Tasha\'s Hideous Laughter'],
          3: ['Detect Thoughts', 'Phantasmal Force'],
          5: ['Clairvoyance', 'Sending'],
          7: ['Dominate Beast', 'Evard\'s Black Tentacles'],
          9: ['Dominate Person', 'Telekinesis']
        },
        features: {
          1: ['Awakened Mind'],
          6: ['Entropic Ward'],
          10: ['Thought Shield'],
          14: ['Create Thrall']
        }
      },
      // [SOURCES]: Available when Tasha's is enabled
      'The Fathomless': {
        description: 'Your patron is a powerful entity of the deep seas. You can command water and call upon tentacles from the depths.',
        expandedSpellList: {
          1: ['Create or Destroy Water', 'Thunderwave'],
          3: ['Gust of Wind', 'Silence'],
          5: ['Lightning Bolt', 'Sleet Storm'],
          7: ['Control Water', 'Summon Elemental'],
          9: ['Bigby\'s Hand', 'Cone of Cold']
        },
        features: {
          1: ['Tentacle of the Deeps', 'Gift of the Sea'],
          6: ['Oceanic Soul', 'Guardian Coil'],
          10: ['Grasping Tentacles'],
          14: ['Fathomless Plunge']
        }
      },
      'The Genie': {
        description: 'Your patron is a noble genie from an Elemental Plane. You gain abilities based on your genie\'s elemental nature.',
        expandedSpellList: {
          1: ['Detect Evil and Good', 'Thunderwave'],
          3: ['Phantasmal Force', 'Scorching Ray'],
          5: ['Create Food and Water', 'Wind Wall'],
          7: ['Phantasmal Killer', 'Wall of Fire'],
          9: ['Creation', 'Seeming']
        },
        features: {
          1: ['Genie\'s Vessel', 'Genie\'s Wrath'],
          6: ['Elemental Gift'],
          10: ['Sanctuary Vessel'],
          14: ['Limited Wish']
        }
      },
      // [SOURCES]: Available when Xanathar's is enabled
      'The Celestial': {
        description: 'Your patron is a powerful being from the Upper Planes. You gain healing abilities and radiant power.',
        expandedSpellList: {
          1: ['Cure Wounds', 'Guiding Bolt'],
          3: ['Flaming Sphere', 'Lesser Restoration'],
          5: ['Daylight', 'Revivify'],
          7: ['Guardian of Faith', 'Wall of Fire'],
          9: ['Flame Strike', 'Greater Restoration']
        },
        features: {
          1: ['Bonus Cantrips', 'Healing Light'],
          6: ['Radiant Soul'],
          10: ['Celestial Resilience'],
          14: ['Searing Vengeance']
        }
      },
      'The Hexblade': {
        description: 'Your patron is a mysterious entity from the Shadowfell. You gain the ability to curse your foes and wield weapons with supernatural skill.',
        // [PHASE B / #023 — 2026-05-06]: Combat-toggle ownership.
        //   Hexblade's Curse (L1) gives +prof bonus damage and crit-on-19
        //   against the cursed target; the toggle marks "this is my
        //   currently-cursed target."
        combatToggles: [
          { flag: 'attackingHexbladeCursedTarget' },
        ],
        expandedSpellList: {
          1: ['Shield', 'Wrathful Smite'],
          3: ['Blur', 'Branding Smite'],
          5: ['Blink', 'Elemental Weapon'],
          7: ['Phantasmal Killer', 'Staggering Smite'],
          9: ['Banishing Smite', 'Cone of Cold']
        },
        features: {
          1: ['Hexblade\'s Curse', 'Hex Warrior'],
          6: ['Accursed Specter'],
          10: ['Armor of Hexes'],
          14: ['Master of Hexes']
        }
      }
    }
  },
  'Wizard': {
    hitDice: '1d6',
    subclassLevel: 2,
    startingGold: '4d4',
    primaryAbility: 'Intelligence',
    savingThrows: ['int', 'wis'],
    // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
    //   PHB p.115: Spell Mastery at L18 (1×L1 + 1×L2 spellbook),
    //   Signature Spells at L20 (2×L3 spellbook).
    // [Spec A Task 5 — 2026-05-16]: subPicks sequentializes Spell Mastery
    //   into "pick L1, then pick L2" (vs. the pre-Spec-A behavior where
    //   the player could pick 2 L1s or 2 L2s — both wrong per PHB p.115).
    //   Signature Spells uses subPicks too for shape parity, even though
    //   it's a single-level pick of count 2.
    levelProgression: [
      { atLevel: 18, action: 'spellMastery', dialogTitle: 'Spell Mastery (1st + 2nd level)', classFilter: 'Wizard', fromSpellbookOnly: true,
        subPicks: [
          { minSpellLevel: 1, maxSpellLevel: 1, count: 1, label: 'Pick 1 1st-level spell from your spellbook' },
          { minSpellLevel: 2, maxSpellLevel: 2, count: 1, label: 'Pick 1 2nd-level spell from your spellbook' },
        ],
      },
      { atLevel: 20, action: 'signatureSpells', dialogTitle: 'Signature Spells (3rd level)', classFilter: 'Wizard', fromSpellbookOnly: true,
        subPicks: [
          { minSpellLevel: 3, maxSpellLevel: 3, count: 2, label: 'Pick 2 3rd-level spells from your spellbook' },
        ],
      },
    ],
    proficiencies: {
      armor: [],
      weapons: ['Daggers', 'Darts', 'Slings', 'Quarterstaffs', 'Light crossbows'],
      tools: [],
      skills: {
        number: 2,
        options: ['Arcana', 'History', 'Insight', 'Investigation', 'Medicine', 'Religion']
      }
    },
    spellcasting: {
      ability: 'int',
      casterType: 'full',
      cantrips: { 1: 3, 4: 4, 10: 5 },
      preparedSpells: 'Intelligence modifier + wizard level',
      spellbook: true
    },
    startingEquipment: [
      { choice: ['Quarterstaff', 'Dagger'] },
      { choice: ['Component pouch', 'Arcane focus'] },
      { choice: ['Scholar\'s pack', 'Explorer\'s pack'] },
      'Spellbook'
    ],
    features: {
      1: ['Spellcasting', 'Arcane Recovery'],
      2: ['Arcane Tradition'],
      3: [],
      4: ['Ability Score Improvement'],
      5: [],
      6: ['Arcane Tradition feature'],
      7: [],
      8: ['Ability Score Improvement'],
      9: [],
      10: ['Arcane Tradition feature'],
      11: [],
      12: ['Ability Score Improvement'],
      13: [],
      14: ['Arcane Tradition feature'],
      15: [],
      16: ['Ability Score Improvement'],
      17: [],
      18: ['Spell Mastery'],
      19: ['Ability Score Improvement'],
      20: ['Signature Spells']
    },
    // [PURPOSE]: D&D 5e SRD Wizard Arcane Traditions (Schools of Magic)
    // [WHY]: Wizards choose their school at level 2
    // [INTEGRATION]: Used by LevelUpWizard and CharacterCreationWizard
    subclasses: {
      'School of Abjuration': {
        description: 'Masters of protective magic. Your study has honed defensive spells and wards to shield yourself and others from harm.',
        features: {
          2: ['Abjuration Savant', 'Arcane Ward'],
          6: ['Projected Ward'],
          10: ['Improved Abjuration'],
          14: ['Spell Resistance']
        }
      },
      'School of Conjuration': {
        description: 'Specialists in summoning creatures and objects from other planes. You focus on creating useful items and bringing allies to your side.',
        features: {
          2: ['Conjuration Savant', 'Minor Conjuration'],
          6: ['Benign Transposition'],
          10: ['Focused Conjuration'],
          14: ['Durable Summons']
        }
      },
      'School of Divination': {
        description: 'Glimpse the future and uncover hidden truths. Your divinations guide your actions and those of your allies.',
        features: {
          2: ['Divination Savant', 'Portent'],
          6: ['Expert Divination'],
          10: ['The Third Eye'],
          14: ['Greater Portent']
        }
      },
      'School of Enchantment': {
        description: 'Masters of beguiling magic that charms and enthralls. You can turn enemies into allies and make others forget your presence.',
        features: {
          2: ['Enchantment Savant', 'Hypnotic Gaze'],
          6: ['Instinctive Charm'],
          10: ['Split Enchantment'],
          14: ['Alter Memories']
        }
      },
      'School of Evocation': {
        description: 'Wielders of raw magical energy that manifests as deadly elemental forces. You sculpt and control destructive power with precision.',
        // [PHASE B / #023 — 2026-05-06]: Combat-toggle ownership.
        //   Empowered Evocation (L10) adds INT mod damage to one damage
        //   roll on an evocation spell cast.
        combatToggles: [
          { flag: 'castingEvocationSpellDamage', minLevel: 10 },
        ],
        features: {
          2: ['Evocation Savant', 'Sculpt Spells'],
          6: ['Potent Cantrip'],
          10: ['Empowered Evocation'],
          14: ['Overchannel']
        }
      },
      'School of Illusion': {
        description: 'Masters of deception and trickery through magical illusions. You make the unreal seem real and can create objects from pure imagination.',
        features: {
          2: ['Illusion Savant', 'Improved Minor Illusion'],
          6: ['Malleable Illusions'],
          10: ['Illusory Self'],
          14: ['Illusory Reality']
        }
      },
      'School of Necromancy': {
        description: 'Explorers of the boundary between life and death. You manipulate life force and command the undead.',
        features: {
          2: ['Necromancy Savant', 'Grim Harvest'],
          6: ['Undead Thralls'],
          10: ['Inured to Undeath'],
          14: ['Command Undead']
        }
      },
      'School of Transmutation': {
        description: 'Experts in transforming matter and energy. You change the fundamental properties of objects, creatures, and even yourself.',
        features: {
          2: ['Transmutation Savant', 'Minor Alchemy'],
          6: ['Transmuter\'s Stone'],
          10: ['Shapechanger'],
          14: ['Master Transmuter']
        }
      }
    }
  }
};

// [MULTICLASS-PROF-SRD-DATA 2026-06-14]: PHB p.163 "Multiclassing Proficiencies"
//   table — the LIMITED proficiency subset gained when you take a level in a class
//   OTHER than your first. Attached onto each SRD class def (not a separate table)
//   so getClassData(name).multiclassProficiencies feeds the MULTICLASS-PROF-APPLY-READER
//   producer (useClassFeatureEffects, index>0) uniformly with custom classes + the
//   Tasha's Artificer (which already carries the field). Source: PHB (2014) p.163,
//   read verbatim from the book. FIXED CATEGORY grants only — the CHOICE-based grants
//   from the same table (Bard "one skill of your choice" + a musical instrument;
//   Rogue/Ranger "one skill from the class's skill list"; Monk's specific "shortswords")
//   are not fixed data -> MULTICLASS-PROF-SRD-CHOICE-GRANTS. Sorcerer + Wizard grant
//   nothing ("—") -> no entry.
const SRD_MULTICLASS_PROFICIENCIES = {
  Barbarian: { armor: ['Shields'], weapons: ['Simple weapons', 'Martial weapons'], tools: [] },
  Bard:      { armor: ['Light armor'], weapons: [], tools: [] },
  Cleric:    { armor: ['Light armor', 'Medium armor', 'Shields'], weapons: [], tools: [] },
  Druid:     { armor: ['Light armor', 'Medium armor', 'Shields'], weapons: [], tools: [] },
  Fighter:   { armor: ['Light armor', 'Medium armor', 'Shields'], weapons: ['Simple weapons', 'Martial weapons'], tools: [] },
  Monk:      { armor: [], weapons: ['Simple weapons'], tools: [] },
  Paladin:   { armor: ['Light armor', 'Medium armor', 'Shields'], weapons: ['Simple weapons', 'Martial weapons'], tools: [] },
  Ranger:    { armor: ['Light armor', 'Medium armor', 'Shields'], weapons: ['Simple weapons', 'Martial weapons'], tools: [] },
  Rogue:     { armor: ['Light armor'], weapons: [], tools: ["Thieves' tools"] },
  Warlock:   { armor: ['Light armor'], weapons: ['Simple weapons'], tools: [] },
};
Object.entries(SRD_MULTICLASS_PROFICIENCIES).forEach(([name, mp]) => {
  if (SRD_CLASSES[name]) SRD_CLASSES[name].multiclassProficiencies = mp;
});

export const SRD_BACKGROUNDS = {
  'Acolyte': {
    skillProficiencies: ['Insight', 'Religion'],
    languages: ['Two of your choice'],
    equipment: [
      'Holy symbol',
      'Prayer book or prayer wheel',
      '5 sticks of incense',
      'Vestments',
      'Set of common clothes',
      'Belt pouch containing 15 gp'
    ],
    feature: 'Shelter of the Faithful',
    // [FIX JAN 12, 2026]: Added feature descriptions for background details panel
    featureDescription: 'As an acolyte, you command the respect of those who share your faith, and you can perform religious ceremonies. You and your adventuring companions can expect to receive free healing and care at a temple, shrine, or other established presence of your faith, though you must provide any material components needed for spells. Those who share your religion will support you (but only you) at a modest lifestyle. You might also have ties to a specific temple dedicated to your chosen deity or pantheon, and you have a residence there.',
    personalityTraits: [
      'I idolize a particular hero of my faith, and constantly refer to that person\'s deeds and example.',
      'I can find common ground between the fiercest enemies, empathizing with them and always working toward peace.',
      'I see omens in every event and action. The gods try to speak to us, we just need to listen.',
      'Nothing can shake my optimistic attitude.',
      'I quote (or misquote) sacred texts and proverbs in almost every situation.',
      'I am tolerant (or intolerant) of other faiths and respect (or condemn) the worship of other gods.',
      'I\'ve enjoyed fine food, drink, and high society among my temple\'s elite. Rough living grates on me.',
      'I\'ve spent so long in the temple that I have little practical experience dealing with people in the outside world.'
    ]
  },
  'Criminal': {
    skillProficiencies: ['Deception', 'Stealth'],
    toolProficiencies: ['Gaming set', 'Thieves\' tools'],
    equipment: [
      'Crowbar',
      'Set of dark common clothes including hood',
      'Belt pouch containing 15 gp'
    ],
    feature: 'Criminal Contact',
    featureDescription: 'You have a reliable and trustworthy contact who acts as your liaison to a network of other criminals. You know how to get messages to and from your contact, even over great distances; specifically, you know the local messengers, corrupt caravan masters, and seedy sailors who can deliver messages for you.',
    variant: 'Spy',
    personalityTraits: [
      'I always have a plan for what to do when things go wrong.',
      'I am always calm, no matter what the situation. I never raise my voice or let my emotions control me.',
      'The first thing I do in a new place is note the locations of everything valuable—or where such things could be hidden.',
      'I would rather make a new friend than a new enemy.',
      'I am incredibly slow to trust. Those who seem the fairest often have the most to hide.',
      'I don\'t pay attention to the risks in a situation. Never tell me the odds.',
      'The best way to get me to do something is to tell me I can\'t do it.',
      'I blow up at the slightest insult.'
    ]
  },
  'Folk Hero': {
    skillProficiencies: ['Animal Handling', 'Survival'],
    toolProficiencies: ['Artisan\'s tools', 'Vehicles (land)'],
    equipment: [
      'Artisan\'s tools',
      'Shovel',
      'Iron pot',
      'Set of common clothes',
      'Belt pouch containing 10 gp'
    ],
    feature: 'Rustic Hospitality',
    featureDescription: 'Since you come from the ranks of the common folk, you fit in among them with ease. You can find a place to hide, rest, or recuperate among other commoners, unless you have shown yourself to be a danger to them. They will shield you from the law or anyone else searching for you, though they will not risk their lives for you.',
    personalityTraits: [
      'I judge people by their actions, not their words.',
      'If someone is in trouble, I\'m always ready to lend help.',
      'When I set my mind to something, I follow through no matter what gets in my way.',
      'I have a strong sense of fair play and always try to find the most equitable solution to arguments.',
      'I\'m confident in my own abilities and do what I can to instill confidence in others.',
      'Thinking is for other people. I prefer action.',
      'I misuse long words in an attempt to sound smarter.',
      'I get bored easily. When am I going to get on with my destiny?'
    ]
  },
  'Noble': {
    skillProficiencies: ['History', 'Persuasion'],
    toolProficiencies: ['Gaming set'],
    languages: ['One of your choice'],
    equipment: [
      'Set of fine clothes',
      'Signet ring',
      'Scroll of pedigree',
      'Purse containing 25 gp'
    ],
    feature: 'Position of Privilege',
    featureDescription: 'Thanks to your noble birth, people are inclined to think the best of you. You are welcome in high society, and people assume you have the right to be wherever you are. The common folk make every effort to accommodate you and avoid your displeasure, and other people of high birth treat you as a member of the same social sphere. You can secure an audience with a local noble if you need to.',
    variant: 'Knight',
    personalityTraits: [
      'My eloquent flattery makes everyone I talk to feel like the most wonderful and important person in the world.',
      'The common folk love me for my kindness and generosity.',
      'No one could doubt by looking at my regal bearing that I am a cut above the unwashed masses.',
      'I take great pains to always look my best and follow the latest fashions.',
      'I don\'t like to get my hands dirty, and I won\'t be caught dead in unsuitable accommodations.',
      'Despite my noble birth, I do not place myself above other folk. We all have the same blood.',
      'My favor, once lost, is lost forever.',
      'If you do me an injury, I will crush you, ruin your name, and salt your fields.'
    ]
  },
  'Sage': {
    skillProficiencies: ['Arcana', 'History'],
    languages: ['Two of your choice'],
    equipment: [
      'Bottle of black ink',
      'Quill',
      'Small knife',
      'Letter from dead colleague',
      'Set of common clothes',
      'Belt pouch containing 10 gp'
    ],
    feature: 'Researcher',
    featureDescription: 'When you attempt to learn or recall a piece of lore, if you do not know that information, you often know where and from whom you can obtain it. Usually, this information comes from a library, scriptorium, university, or a sage or other learned person or creature. Your DM might rule that the knowledge you seek is secreted away in an almost inaccessible place, or that it simply cannot be found.',
    personalityTraits: [
      'I use polysyllabic words that convey the impression of great erudition.',
      'I\'ve read every book in the world\'s greatest libraries—or I like to boast that I have.',
      'I\'m used to helping out those who aren\'t as smart as I am, and I patiently explain anything and everything to others.',
      'There\'s nothing I like more than a good mystery.',
      'I\'m willing to listen to every side of an argument before I make my own judgment.',
      'I . . . speak . . . slowly . . . when talking . . . to idiots, . . . which . . . almost . . . everyone . . . is . . . compared . . . to me.',
      'I am horribly, horribly awkward in social situations.',
      'I\'m convinced that people are always trying to steal my secrets.'
    ]
  },
  'Soldier': {
    skillProficiencies: ['Athletics', 'Intimidation'],
    toolProficiencies: ['Gaming set', 'Vehicles (land)'],
    equipment: [
      'Insignia of rank',
      'Trophy from fallen enemy',
      'Deck of cards or dice set',
      'Set of common clothes',
      'Belt pouch containing 10 gp'
    ],
    feature: 'Military Rank',
    featureDescription: 'You have a military rank from your career as a soldier. Soldiers loyal to your former military organization still recognize your authority and influence, and they defer to you if they are of a lower rank. You can invoke your rank to exert influence over other soldiers and requisition simple equipment or horses for temporary use. You can also usually gain access to friendly military encampments and fortresses where your rank is recognized.',
    personalityTraits: [
      'I\'m always polite and respectful.',
      'I\'m haunted by memories of war. I can\'t get the images of violence out of my mind.',
      'I\'ve lost too many friends, and I\'m slow to make new ones.',
      'I\'m full of inspiring and cautionary tales from my military experience relevant to almost every combat situation.',
      'I can stare down a hell hound without flinching.',
      'I enjoy being strong and like breaking things.',
      'I have a crude sense of humor.',
      'I face problems head-on. A simple, direct solution is the best path to success.'
    ]
  },
  'Charlatan': {
    skillProficiencies: ['Deception', 'Sleight of Hand'],
    toolProficiencies: ['Disguise kit', 'Forgery kit'],
    equipment: [
      'Set of fine clothes',
      'Disguise kit',
      'Con tools (weighted dice, marked cards, signet ring)',
      'Belt pouch containing 15 gp'
    ],
    feature: 'False Identity',
    featureDescription: 'You have created a second identity that includes documentation, established acquaintances, and disguises that allow you to assume that persona. Additionally, you can forge documents including official papers and personal letters, as long as you have seen an example of the kind of document or the handwriting you are trying to copy.',
    personalityTraits: [
      'I fall in and out of love easily, and am always pursuing someone.',
      'I have a joke for every occasion, especially occasions where humor is inappropriate.',
      'Flattery is my preferred trick for getting what I want.',
      'I\'m a born gambler who can\'t resist taking a risk for a potential payoff.',
      'I lie about almost everything, even when there\'s no good reason to.',
      'Sarcasm and insults are my weapons of choice.',
      'I keep multiple holy symbols on me and invoke whatever deity might come in useful at any given moment.',
      'I pocket anything I see that might have some value.'
    ]
  },
  'Entertainer': {
    skillProficiencies: ['Acrobatics', 'Performance'],
    toolProficiencies: ['Disguise kit', 'Musical instrument'],
    equipment: [
      'Musical instrument',
      'Favor of an admirer',
      'Costume',
      'Belt pouch containing 15 gp'
    ],
    feature: 'By Popular Demand',
    featureDescription: 'You can always find a place to perform, usually in an inn or tavern but possibly with a circus, at a theater, or even in a noble\'s court. At such a place, you receive free lodging and food of a modest or comfortable standard (depending on the quality of the establishment), as long as you perform each night. In addition, your performance makes you something of a local figure. When strangers recognize you in a town where you have performed, they typically take a liking to you.',
    variant: 'Gladiator',
    personalityTraits: [
      'I know a story relevant to almost every situation.',
      'Whenever I come to a new place, I collect local rumors and spread gossip.',
      'I\'m a hopeless romantic, always searching for that "special someone."',
      'Nobody stays angry at me or around me for long, since I can defuse any amount of tension.',
      'I love a good insult, even one directed at me.',
      'I get bitter if I\'m not the center of attention.',
      'I\'ll settle for nothing less than perfection.',
      'I change my mood or my mind as quickly as I change key in a song.'
    ]
  },
  'Guild Artisan': {
    skillProficiencies: ['Insight', 'Persuasion'],
    toolProficiencies: ['Artisan\'s tools'],
    languages: ['One of your choice'],
    equipment: [
      'Artisan\'s tools',
      'Letter of introduction from guild',
      'Set of traveler\'s clothes',
      'Belt pouch containing 15 gp'
    ],
    feature: 'Guild Membership',
    featureDescription: 'As an established and respected member of a guild, you can rely on certain benefits that membership provides. Your fellow guild members will provide you with lodging and food if necessary, and pay for your funeral if needed. In some cities and towns, a guildhall offers a central place to meet other members of your profession, which can be a good place to meet potential patrons, allies, or hirelings. Guilds often wield tremendous political power. You must pay dues of 5 gp per month to the guild.',
    variant: 'Guild Merchant',
    personalityTraits: [
      'I believe that anything worth doing is worth doing right. I can\'t help it—I\'m a perfectionist.',
      'I\'m a snob who looks down on those who can\'t appreciate fine art.',
      'I always want to know how things work and what makes people tick.',
      'I\'m full of witty aphorisms and have a proverb for every occasion.',
      'I\'m rude to people who lack my commitment to hard work and fair play.',
      'I like to talk at length about my profession.',
      'I don\'t part with my money easily and will haggle tirelessly to get the best deal possible.',
      'I\'m well known for my work, and I want to make sure everyone appreciates it. I\'m always taken aback when people haven\'t heard of me.'
    ]
  },
  'Hermit': {
    skillProficiencies: ['Medicine', 'Religion'],
    toolProficiencies: ['Herbalism kit'],
    languages: ['One of your choice'],
    equipment: [
      'Scroll case with notes',
      'Winter blanket',
      'Set of common clothes',
      'Herbalism kit',
      'Belt pouch containing 5 gp'
    ],
    feature: 'Discovery',
    featureDescription: 'The quiet seclusion of your extended hermitage gave you access to a unique and powerful discovery. The exact nature of this revelation depends on the nature of your seclusion. It might be a great truth about the cosmos, the deities, the powerful beings of the outer planes, or the forces of nature. It could be a site that no one else has ever seen. You might have uncovered a fact that has long been forgotten, or unearthed some relic of the past that could rewrite history. Work with your DM to determine the details of your discovery and its impact on the campaign.',
    personalityTraits: [
      'I\'ve been isolated for so long that I rarely speak, preferring gestures and the occasional grunt.',
      'I am utterly serene, even in the face of disaster.',
      'The leader of my community had something wise to say on every topic, and I am eager to share that wisdom.',
      'I feel tremendous empathy for all who suffer.',
      'I\'m oblivious to etiquette and social expectations.',
      'I connect everything that happens to me to a grand, cosmic plan.',
      'I often get lost in my own thoughts and contemplation, becoming oblivious to my surroundings.',
      'I am working on a grand philosophical theory and love sharing my ideas.'
    ]
  },
  'Outlander': {
    skillProficiencies: ['Athletics', 'Survival'],
    toolProficiencies: ['Musical instrument'],
    languages: ['One of your choice'],
    equipment: [
      'Staff',
      'Hunting trap',
      'Trophy from animal',
      'Set of traveler\'s clothes',
      'Belt pouch containing 10 gp'
    ],
    feature: 'Wanderer',
    featureDescription: 'You have an excellent memory for maps and geography, and you can always recall the general layout of terrain, settlements, and other features around you. In addition, you can find food and fresh water for yourself and up to five other people each day, provided that the land offers berries, small game, water, and so forth.',
    personalityTraits: [
      'I\'m driven by a wanderlust that led me away from home.',
      'I watch over my friends as if they were a litter of newborn pups.',
      'I once ran twenty-five miles without stopping to warn my clan of an approaching orc horde. I\'d do it again if I had to.',
      'I have a lesson for every situation, drawn from observing nature.',
      'I place no stock in wealthy or well-mannered folk. Money and manners won\'t save you from a hungry owlbear.',
      'I\'m always picking things up, absently fiddling with them, and sometimes accidentally breaking them.',
      'I feel far more comfortable around animals than people.',
      'I was, in fact, raised by wolves.'
    ]
  },
  'Sailor': {
    skillProficiencies: ['Athletics', 'Perception'],
    toolProficiencies: ['Navigator\'s tools', 'Vehicles (water)'],
    equipment: [
      'Belaying pin',
      '50 feet of silk rope',
      'Lucky charm',
      'Set of common clothes',
      'Belt pouch containing 10 gp'
    ],
    feature: 'Ship\'s Passage',
    featureDescription: 'When you need to, you can secure free passage on a sailing ship for yourself and your adventuring companions. You might sail on the ship you served on, or another ship you have good relations with (perhaps one captained by a former crewmate). Because you\'re calling in a favor, you can\'t be certain of a schedule or route that will meet your every need. Your DM will determine how long it takes to get where you need to go. In return for your free passage, you and your companions are expected to assist the crew during the voyage.',
    variant: 'Pirate',
    personalityTraits: [
      'My friends know they can rely on me, no matter what.',
      'I work hard so that I can play hard when the work is done.',
      'I enjoy sailing into new ports and making new friends over a flagon of ale.',
      'I stretch the truth for the sake of a good story.',
      'To me, a tavern brawl is a nice way to get to know a new city.',
      'I never pass up a friendly wager.',
      'My language is as foul as an otyugh nest.',
      'I like a job well done, especially if I can convince someone else to do it.'
    ]
  },
  'Urchin': {
    skillProficiencies: ['Sleight of Hand', 'Stealth'],
    toolProficiencies: ['Disguise kit', 'Thieves\' tools'],
    equipment: [
      'Small knife',
      'Map of your home city',
      'Pet mouse',
      'Token to remember parents',
      'Set of common clothes',
      'Belt pouch containing 10 gp'
    ],
    feature: 'City Secrets',
    featureDescription: 'You know the secret patterns and flow to cities and can find passages through the urban sprawl that others would miss. When you are not in combat, you (and companions you lead) can travel between any two locations in the city twice as fast as your speed would normally allow.',
    personalityTraits: [
      'I hide scraps of food and trinkets away in my pockets.',
      'I ask a lot of questions.',
      'I like to squeeze into small places where no one else can get to me.',
      'I sleep with my back to a wall or tree, with everything I own wrapped in a bundle in my arms.',
      'I eat like a pig and have bad manners.',
      'I think anyone who\'s nice to me is hiding evil intent.',
      'I don\'t like to bathe.',
      'I bluntly say what other people are hinting at or hiding.'
    ]
  }
};

// Helper function to get race data with subraces
export function getRaceData(raceName, subraceName = null) {
  const race = SRD_RACES[raceName];
  if (!race) return null;

  let data = { ...race };

  if (subraceName && race.subraces && race.subraces[subraceName]) {
    const subrace = race.subraces[subraceName];
    // Merge subrace ability scores
    if (subrace.abilityScoreIncrease) {
      data.abilityScoreIncrease = {
        ...data.abilityScoreIncrease,
        ...subrace.abilityScoreIncrease
      };
    }
    // Merge features
    if (subrace.features) {
      data.features = [...data.features, ...subrace.features];
    }
    // Merge proficiencies
    if (subrace.proficiencies) {
      data.proficiencies = {
        ...(data.proficiencies || {}),
        ...subrace.proficiencies
      };
    }
    // Apply other subrace properties
    if (subrace.speed) data.speed = subrace.speed;
    if (subrace.darkvision) data.darkvision = subrace.darkvision;
    if (subrace.languages) data.languages = [...data.languages, ...subrace.languages];
    if (subrace.hitPointBonus) data.hitPointBonus = subrace.hitPointBonus;
    if (subrace.savingThrows) {
      data.savingThrows = {
        ...(data.savingThrows || {}),
        ...subrace.savingThrows
      };
    }
  }

  return data;
}

// Helper function to get class features for a specific level
export function getClassFeaturesAtLevel(className, level) {
  // [v1.0]: Check SRD first, then fall back — contentResolver can't be used here
  // to avoid circular imports (contentResolver imports from this file)
  const classData = SRD_CLASSES[className];
  if (!classData) return null;

  const features = [];
  for (let i = 1; i <= level; i++) {
    if (classData.features[i]) {
      features.push(...classData.features[i]);
    }
  }

  return {
    ...classData,
    currentFeatures: features,
    level: level
  };
}

// Helper function to calculate spell slots based on class and level
export function getSpellSlots(className, level) {
  // [v1.0]: Read caster type from class data instead of hardcoded arrays
  const classData = SRD_CLASSES[className];
  const casterType = classData?.spellcasting?.casterType || null;

  if (casterType === 'pact') {
    // Warlock pact magic slots
    const pactSlots = {
      1: { slots: 1, level: 1 },
      2: { slots: 2, level: 1 },
      3: { slots: 2, level: 2 },
      5: { slots: 2, level: 3 },
      7: { slots: 2, level: 4 },
      9: { slots: 2, level: 5 },
      11: { slots: 3, level: 5 },
      17: { slots: 4, level: 5 }
    };

    for (let i = level; i >= 1; i--) {
      if (pactSlots[i]) {
        return { pactSlots: pactSlots[i].slots, slotLevel: pactSlots[i].level };
      }
    }
  }

  // Standard spell slot progression
  const spellSlotTable = {
    1: [2, 0, 0, 0, 0, 0, 0, 0, 0],
    2: [3, 0, 0, 0, 0, 0, 0, 0, 0],
    3: [4, 2, 0, 0, 0, 0, 0, 0, 0],
    4: [4, 3, 0, 0, 0, 0, 0, 0, 0],
    5: [4, 3, 2, 0, 0, 0, 0, 0, 0],
    6: [4, 3, 3, 0, 0, 0, 0, 0, 0],
    7: [4, 3, 3, 1, 0, 0, 0, 0, 0],
    8: [4, 3, 3, 2, 0, 0, 0, 0, 0],
    9: [4, 3, 3, 3, 1, 0, 0, 0, 0],
    10: [4, 3, 3, 3, 2, 0, 0, 0, 0],
    11: [4, 3, 3, 3, 2, 1, 0, 0, 0],
    12: [4, 3, 3, 3, 2, 1, 0, 0, 0],
    13: [4, 3, 3, 3, 2, 1, 1, 0, 0],
    14: [4, 3, 3, 3, 2, 1, 1, 0, 0],
    15: [4, 3, 3, 3, 2, 1, 1, 1, 0],
    16: [4, 3, 3, 3, 2, 1, 1, 1, 0],
    17: [4, 3, 3, 3, 2, 1, 1, 1, 1],
    18: [4, 3, 3, 3, 3, 1, 1, 1, 1],
    19: [4, 3, 3, 3, 3, 2, 1, 1, 1],
    20: [4, 3, 3, 3, 3, 2, 2, 1, 1]
  };

  let casterLevel = 0;

  if (casterType === 'full') {
    casterLevel = level;
  } else if (casterType === 'half') {
    // [FIX APR 2026 — AUDIT C-2]: Artificer rounds UP for caster level (TCoE p.9)
    const roundUp = classData?.spellcasting?.halfCasterRoundUp;
    casterLevel = roundUp ? Math.ceil(level / 2) : Math.floor(level / 2);
  } else if (casterType === 'third') {
    casterLevel = Math.floor(level / 3);
  } else if (!casterType) {
    // [FIX APR 2026 — AUDIT C-2]: Class not in SRD_CLASSES (e.g., Artificer in Tasha's)
    // Use lazy require to avoid circular dependency with contentResolver
    try {
      const { getClassData, getCasterType } = require('../utils/contentResolver');
      const resolvedData = getClassData(className);
      const resolvedCasterType = getCasterType(className);
      if (resolvedCasterType === 'full') {
        casterLevel = level;
      } else if (resolvedCasterType === 'half') {
        casterLevel = resolvedData?.spellcasting?.halfCasterRoundUp
          ? Math.ceil(level / 2)
          : Math.floor(level / 2);
      } else if (resolvedCasterType === 'third') {
        casterLevel = Math.floor(level / 3);
      } else if (resolvedCasterType === 'pact') {
        // Warlock pact magic handled above
        return null;
      } else {
        return null;
      }
    } catch {
      return null; // contentResolver not available yet
    }
  } else {
    return null; // Non-caster or requires archetype check
  }

  if (casterLevel === 0 || !spellSlotTable[casterLevel]) {
    return null;
  }

  const slots = {};
  spellSlotTable[casterLevel].forEach((count, index) => {
    if (count > 0) {
      slots[index + 1] = count;
    }
  });

  return slots;
}

// Helper function to get all available subraces for a race
export function getSubraces(raceName) {
  const race = SRD_RACES[raceName];
  if (!race || !race.subraces) return []; // Return empty array, not null
  return Object.keys(race.subraces);
}

// Helper function to get starting equipment based on class choices
export function getStartingEquipment(className, choices = {}) {
  const classData = SRD_CLASSES[className];
  if (!classData || !classData.startingEquipment) return [];

  const equipment = [];

  classData.startingEquipment.forEach((item, index) => {
    if (typeof item === 'string') {
      equipment.push(item);
    } else if (item.choice && choices[index]) {
      equipment.push(choices[index]);
    } else if (item.choice) {
      equipment.push(item.choice[0]); // Default to first choice
    }
  });

  return equipment;
}

// [PURPOSE]: D&D 5e Fighting Styles for Fighter, Paladin, and Ranger
// [WHY]: These classes get Fighting Style feature at level 1 (Fighter/Paladin) or level 2 (Ranger)
// [INTEGRATION]: Used by CharacterCreationWizard and LevelUpWizard
export const FIGHTING_STYLES = {
  'Archery': {
    name: 'Archery',
    description: 'You gain a +2 bonus to attack rolls you make with ranged weapons.',
    effect: { attackBonus: { ranged: 2 } },
    classes: ['Fighter', 'Ranger']
  },
  'Defense': {
    name: 'Defense',
    description: 'While you are wearing armor, you gain a +1 bonus to AC.',
    effect: { acBonus: 1, requiresArmor: true },
    classes: ['Fighter', 'Paladin', 'Ranger']
  },
  'Dueling': {
    name: 'Dueling',
    description: 'When you are wielding a melee weapon in one hand and no other weapons, you gain a +2 bonus to damage rolls with that weapon.',
    effect: { damageBonus: { melee: 2, condition: 'one_hand_only' } },
    classes: ['Fighter', 'Paladin', 'Ranger']
  },
  'Great Weapon Fighting': {
    name: 'Great Weapon Fighting',
    description: 'When you roll a 1 or 2 on a damage die for an attack you make with a melee weapon that you are wielding with two hands, you can reroll the die and must use the new roll. The weapon must have the two-handed or versatile property.',
    effect: { rerollOnes: true, rerollTwos: true, condition: 'two_handed' },
    classes: ['Fighter', 'Paladin']
  },
  'Protection': {
    name: 'Protection',
    description: 'When a creature you can see attacks a target other than you that is within 5 feet of you, you can use your reaction to impose disadvantage on the attack roll. You must be wielding a shield.',
    effect: { protectionReaction: true, requiresShield: true },
    classes: ['Fighter', 'Paladin']
  },
  'Two-Weapon Fighting': {
    name: 'Two-Weapon Fighting',
    description: 'When you engage in two-weapon fighting, you can add your ability modifier to the damage of the second attack.',
    effect: { addModifierToOffhand: true },
    classes: ['Fighter', 'Ranger']
  }
};

// Helper function to get available fighting styles for a class
export function getAvailableFightingStyles(className) {
  return Object.keys(FIGHTING_STYLES).filter(style =>
    FIGHTING_STYLES[style].classes.includes(className)
  );
}

// [CRITICAL-5 - NOV 15, 2025]: Extract all SRD subclasses into flat structure for sources system
// [PURPOSE]: Allow sourcesManager to initialize SRD subclasses (40 total from 12 classes)
// [WHY]: Subclasses need to be in sources system alongside Tasha's (19) and Xanathar's (29) subclasses
// [NOTE]: This dynamically extracts from SRD_CLASSES to avoid duplication
export const SRD_SUBCLASSES = (() => {
  const subclasses = {};

  // Map of when each class chooses their subclass
  const subclassLevels = {
    'Barbarian': 3,
    'Bard': 3,
    'Cleric': 1,
    'Druid': 2,
    'Fighter': 3,
    'Monk': 3,
    'Paladin': 3,
    'Ranger': 3,
    'Rogue': 3,
    'Sorcerer': 1,
    'Warlock': 1,
    'Wizard': 2
  };

  for (const [className, classData] of Object.entries(SRD_CLASSES)) {
    if (classData.subclasses) {
      for (const [subclassName, subclassData] of Object.entries(classData.subclasses)) {
        // Create key (lowercase, underscores)
        const subclassKey = subclassName.toLowerCase().replace(/[^a-z0-9]/g, '_');

        subclasses[subclassKey] = {
          name: subclassName,
          class: className,
          source: 'srd',
          level: subclassLevels[className] || 3,
          ...subclassData
        };
      }
    }
  }

  return subclasses;
})();

// Export lists for dropdowns
export const RACE_LIST = Object.keys(SRD_RACES);
export const CLASS_LIST = Object.keys(SRD_CLASSES);
export const BACKGROUND_LIST = Object.keys(SRD_BACKGROUNDS);
export const SUBCLASS_LIST = Object.keys(SRD_SUBCLASSES); // [CRITICAL-5]: Added subclass list